import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  FaEye,
  FaPrint,
  FaFileExcel,
  FaFilePdf,
  FaTrash,
  FaPlus,
  FaTimes,
  FaSearch,
} from "react-icons/fa";
import { createPortal } from "react-dom";
import axiosInstance from "../../../../shared/utils.js/axiosConfig";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import { useTitle } from "../../../../shared/hooks/TitleContext";
import i18n from "../../../../i18n/i18n";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
  getPdfRowStyles,
  chunkArray,
  extractArray,
} from "../../../../shared/utils.js/utils";
import {
  getProjectsForReport,
  getLabTestsForReport,
  clearError,
  clearSuccess,
} from "../../../../redux/Slices/ProjectReportsSlice";
import {
  getCustomers,
  getTaxes,
  getCostCenters,
  addTax,
  addCostCenter,
  addCustomer,
  clearError as clearAuthError,
  clearSuccess as clearAuthSuccess,
} from "../../../../redux/Slices/authSlice";
import PdfPreviewModal from "../../Accounting/Reports/components/PdfPreviewModal";

// ─── Standalone DropdownWithSearch (must be outside component to avoid hooks violation) ───
const DropdownWithSearch = ({
  isOpen,
  onToggle,
  searchTerm,
  onSearchChange,
  searchInputRef,
  items,
  pagination,
  currentPage,
  onPageChange,
  onSelect,
  label,
  renderItem,
  isRTL,
  t,
}) => {
  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchInputRef]);

  if (!isOpen) return null;

  const safeItems = extractArray(items);

  const renderDropdownItemDefault = item => {
    const code = item.code || "";
    const name = isRTL
      ? item.name_ar || item.name_en || item.name || ""
      : item.name_en || item.name_ar || item.name || "";
    return `${code ? `${code} - ` : ""}${name}`;
  };

  return (
    <div
      style={{
        position: "absolute",
        zIndex: 10,
        top: "100%",
        left: 0,
        right: 0,
        background: "#1a1f2e",
        border: "1px solid #333",
        borderRadius: 8,
        marginTop: 4,
        maxHeight: 400,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ padding: 8, flexShrink: 0 }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={`${t ? t("projectReports.search", { defaultValue: "ابحث عن" }) : "ابحث عن"} ${label || ""}`}
          value={searchTerm || ""}
          onChange={e => {
            onSearchChange && onSearchChange(e.target.value);
          }}
          onFocus={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
          autoFocus
          style={{
            width: "100%",
            padding: 10,
            borderRadius: 6,
            border: "1px solid #333",
            backgroundColor: "#14263b",
            color: "white",
            fontSize: 14,
            direction: isRTL ? "rtl" : "ltr",
            textAlign: isRTL ? "right" : "left",
          }}
        />
      </div>
      {pagination && pagination.last_page >= 1 && (
        <div
          style={{
            padding: "8px",
            borderTop: "1px solid #333",
            background: "#14263b",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 6,
            }}
          >
            <span style={{ color: "#ccc", fontSize: 11 }}>
              {t ? t("projectReports.page", { defaultValue: "صفحة" }) : "صفحة"}{" "}
              {currentPage || 1}{" "}
              {t ? t("projectReports.of", { defaultValue: "من" }) : "من"}{" "}
              {pagination.last_page}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 4,
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                if ((currentPage || 1) > 1) onPageChange && onPageChange(1);
              }}
              disabled={(currentPage || 1) === 1}
              style={{
                padding: "4px 8px",
                backgroundColor:
                  (currentPage || 1) === 1 ? "#2a2a2a" : "#1a1f2e",
                color: (currentPage || 1) === 1 ? "#666" : "white",
                border: "1px solid #444",
                borderRadius: "4px",
                cursor: (currentPage || 1) === 1 ? "not-allowed" : "pointer",
                fontSize: 10,
                minWidth: "28px",
              }}
            >
              ««
            </button>
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                if ((currentPage || 1) > 1)
                  onPageChange && onPageChange((currentPage || 1) - 1);
              }}
              disabled={(currentPage || 1) === 1}
              style={{
                padding: "4px 8px",
                backgroundColor:
                  (currentPage || 1) === 1 ? "#2a2a2a" : "#1a1f2e",
                color: (currentPage || 1) === 1 ? "#666" : "white",
                border: "1px solid #444",
                borderRadius: "4px",
                cursor: (currentPage || 1) === 1 ? "not-allowed" : "pointer",
                fontSize: 10,
                minWidth: "28px",
              }}
            >
              ‹
            </button>
            {(() => {
              const pages = [];
              const cur = currentPage || 1;
              const last = pagination.last_page;
              let start = Math.max(1, cur - 2);
              let end = Math.min(last, start + 4);
              if (end - start < 4) start = Math.max(1, end - 4);
              for (let i = start; i <= end; i++) pages.push(i);
              return pages.map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    onPageChange && onPageChange(p);
                  }}
                  style={{
                    padding: "4px 8px",
                    backgroundColor: p === cur ? "#AC2000" : "#1a1f2e",
                    color: "white",
                    border: p === cur ? "1px solid #AC2000" : "1px solid #444",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: 10,
                    minWidth: "28px",
                  }}
                >
                  {p}
                </button>
              ));
            })()}
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                if ((currentPage || 1) < pagination.last_page)
                  onPageChange && onPageChange((currentPage || 1) + 1);
              }}
              disabled={(currentPage || 1) >= pagination.last_page}
              style={{
                padding: "4px 8px",
                backgroundColor:
                  (currentPage || 1) >= pagination.last_page
                    ? "#2a2a2a"
                    : "#1a1f2e",
                color:
                  (currentPage || 1) >= pagination.last_page ? "#666" : "white",
                border: "1px solid #444",
                borderRadius: "4px",
                cursor:
                  (currentPage || 1) >= pagination.last_page
                    ? "not-allowed"
                    : "pointer",
                fontSize: 10,
                minWidth: "28px",
              }}
            >
              ›
            </button>
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                if ((currentPage || 1) < pagination.last_page)
                  onPageChange && onPageChange(pagination.last_page);
              }}
              disabled={(currentPage || 1) >= pagination.last_page}
              style={{
                padding: "4px 8px",
                backgroundColor:
                  (currentPage || 1) >= pagination.last_page
                    ? "#2a2a2a"
                    : "#1a1f2e",
                color:
                  (currentPage || 1) >= pagination.last_page ? "#666" : "white",
                border: "1px solid #444",
                borderRadius: "4px",
                cursor:
                  (currentPage || 1) >= pagination.last_page
                    ? "not-allowed"
                    : "pointer",
                fontSize: 10,
                minWidth: "28px",
              }}
            >
              »»
            </button>
          </div>
        </div>
      )}
      <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        {safeItems.length === 0 ? (
          <div style={{ padding: 12, textAlign: "center", color: "#bbb" }}>
            {t
              ? t("projectReports.noData", { defaultValue: "لا توجد نتائج" })
              : "لا توجد نتائج"}
          </div>
        ) : (
          safeItems.map(item => (
            <div
              key={item.id}
              onClick={e => {
                e.stopPropagation();
                onSelect && onSelect(item.id);
                onToggle && onToggle(false);
              }}
              style={{
                padding: "10px 12px",
                cursor: "pointer",
                borderTop: "1px solid #333",
                color: "white",
                backgroundColor: "transparent",
                transition: "background-color 0.2s",
                direction: isRTL ? "rtl" : "ltr",
                textAlign: isRTL ? "right" : "left",
              }}
              onMouseEnter={e =>
                (e.currentTarget.style.backgroundColor = "#2a2a2a")
              }
              onMouseLeave={e =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              <span style={{ color: "white" }}>
                {renderItem
                  ? renderItem(item)
                  : renderDropdownItemDefault(item)}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const ProjectTests = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { t } = useTranslation("global");
  const isRTL = i18n.language === "ar";
  const labelAlign = isRTL ? "right" : "left";
  const publicUrl = process.env.PUBLIC_URL || "";
  const headerLogo = `${publicUrl}/ACE.png`;

  const { projects, labTests, error, success } = useSelector(
    state => state.projectReports,
  );
  const {
    customers,
    customersPagination,
    taxes,
    taxesPagination,
    costCenters,
    costCentersPagination,
    error: authError,
    success: authSuccess,
  } = useSelector(state => state.auth);

  const [projectTests, setProjectTests] = useState([]);
  const [meta, setMeta] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const [projectId, setProjectId] = useState("");
  const [labTestId, setLabTestId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState("");
  const [projectPage, setProjectPage] = useState(1);
  const projectSearchInputRef = useRef(null);

  const [labDropdownOpen, setLabDropdownOpen] = useState(false);
  const [labSearchTerm, setLabSearchTerm] = useState("");
  const [labPage, setLabPage] = useState(1);
  const labSearchInputRef = useRef(null);

  const [reportCustomerId, setReportCustomerId] = useState("");
  const [reportCustomerDropdownOpen, setReportCustomerDropdownOpen] =
    useState(false);
  const [reportCustomerSearchTerm, setReportCustomerSearchTerm] =
    useState("");
  const reportCustomerSearchInputRef = useRef(null);

  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null); // 'all' | 'single'
  const [pdfChunks, setPdfChunks] = useState([]); // For paginated PDF generation
  const [previewType, setPreviewType] = useState(null); // 'all' | 'single'
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [modalMode, setModalMode] = useState(null); // 'create' | 'edit' | null

  // Modal form states
  const [modalProjectId, setModalProjectId] = useState("");
  const [modalLabTestId, setModalLabTestId] = useState("");
  const [modalTestDate, setModalTestDate] = useState("");
  const [modalQty, setModalQty] = useState(1);
  const [modalDescription, setModalDescription] = useState("");

  // Modal dropdown states
  const [modalProjectDropdownOpen, setModalProjectDropdownOpen] =
    useState(false);
  const [modalProjectSearchTerm, setModalProjectSearchTerm] = useState("");
  const [modalProjectPage, setModalProjectPage] = useState(1);
  const [modalProjectMeta, setModalProjectMeta] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
  });
  const modalProjectSearchInputRef = useRef(null);

  const [modalLabDropdownOpen, setModalLabDropdownOpen] = useState(false);
  const [modalLabSearchTerm, setModalLabSearchTerm] = useState("");
  const [modalLabPage, setModalLabPage] = useState(1);
  const [modalLabMeta, setModalLabMeta] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
  });
  const modalLabSearchInputRef = useRef(null);

  // Project modal state
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [projectForm, setProjectForm] = useState({
    code: "",
    name_ar: "",
    name_en: "",
    customer_id: null,
    start_date: "",
    end_date: "",
    status: "planned",
    contract_value: "",
    tax_id: null,
    cost_center_id: null,
    notes: "",
  });
  const [isSubmittingProject, setIsSubmittingProject] = useState(false);

  // Test modal state (for adding lab test)
  const [showAddTestModal, setShowAddTestModal] = useState(false);
  const [testForm, setTestForm] = useState({
    name_ar: "",
    name_en: "",
    is_active: true,
  });
  const [isSubmittingTest, setIsSubmittingTest] = useState(false);

  // Project modal dropdown states
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [customerCurrentPage, setCustomerCurrentPage] = useState(1);
  const customerSearchInputRef = useRef(null);
  const customerSearchTimeoutRef = useRef(null);

  const [taxDropdownOpen, setTaxDropdownOpen] = useState(false);
  const [taxSearchTerm, setTaxSearchTerm] = useState("");
  const [taxCurrentPage, setTaxCurrentPage] = useState(1);
  const taxSearchInputRef = useRef(null);
  const taxSearchTimeoutRef = useRef(null);

  const [costCenterDropdownOpen, setCostCenterDropdownOpen] = useState(false);
  const [costCenterSearchTerm, setCostCenterSearchTerm] = useState("");
  const [costCenterCurrentPage, setCostCenterCurrentPage] = useState(1);
  const costCenterSearchInputRef = useRef(null);
  const costCenterSearchTimeoutRef = useRef(null);

  // Tax modal state
  const [showAddTaxModal, setShowAddTaxModal] = useState(false);
  const [taxForm, setTaxForm] = useState({
    name_ar: "",
    name_en: "",
    rate: "",
    is_active: true,
  });
  const [isSubmittingTax, setIsSubmittingTax] = useState(false);

  // Cost center modal state
  const [showAddCostCenterModal, setShowAddCostCenterModal] = useState(false);
  const [costCenterForm, setCostCenterForm] = useState({
    name_ar: "",
    name_en: "",
    code: "",
  });
  const [isSubmittingCostCenter, setIsSubmittingCostCenter] = useState(false);

  // Customer modal state
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [customerForm, setCustomerForm] = useState({
    name_en: "",
    name_ar: "",
    code: "",
    contact_person: "",
    email: "",
    phone: "",
    address: "",
    tax_number: "",
    is_active: true,
    notes: "",
  });
  const [isSubmittingCustomer, setIsSubmittingCustomer] = useState(false);

  useEffect(() => {
    setTitle("projectReports.projectTests");
    dispatch(getProjectsForReport({ per_page: 1000 }));
    dispatch(getLabTestsForReport({ per_page: 1000 }));
    dispatch(getCustomers({ per_page: 1000 }));
    // Fetch all project tests on initial load
    fetchProjectTests({ page: 1 });
    return () => {};
  }, [dispatch, setTitle]);

  // Load customers, taxes, and cost centers when dropdowns open
  useEffect(() => {
    if (customerDropdownOpen) {
      dispatch(
        getCustomers({
          page: customerCurrentPage,
          per_page: 1000,
          search: customerSearchTerm || undefined,
        }),
      );
    }
  }, [customerCurrentPage, customerDropdownOpen, customerSearchTerm, dispatch]);

  useEffect(() => {
    if (customerSearchTerm !== undefined && customerDropdownOpen) {
      if (customerSearchTimeoutRef.current)
        clearTimeout(customerSearchTimeoutRef.current);
      customerSearchTimeoutRef.current = setTimeout(() => {
        setCustomerCurrentPage(1);
        dispatch(
          getCustomers({
            page: 1,
            per_page: 1000,
            search: customerSearchTerm || undefined,
          }),
        );
      }, 300);
    }
    return () => {
      if (customerSearchTimeoutRef.current)
        clearTimeout(customerSearchTimeoutRef.current);
    };
  }, [customerSearchTerm, customerDropdownOpen, dispatch]);

  useEffect(() => {
    if (taxDropdownOpen) {
      dispatch(
        getTaxes({
          page: taxCurrentPage,
          per_page: 1000,
          search: taxSearchTerm || undefined,
        }),
      );
    }
  }, [taxCurrentPage, taxDropdownOpen, taxSearchTerm, dispatch]);

  useEffect(() => {
    if (taxSearchTerm !== undefined && taxDropdownOpen) {
      if (taxSearchTimeoutRef.current)
        clearTimeout(taxSearchTimeoutRef.current);
      taxSearchTimeoutRef.current = setTimeout(() => {
        setTaxCurrentPage(1);
        dispatch(
          getTaxes({
            page: 1,
            per_page: 1000,
            search: taxSearchTerm || undefined,
          }),
        );
      }, 300);
    }
    return () => {
      if (taxSearchTimeoutRef.current)
        clearTimeout(taxSearchTimeoutRef.current);
    };
  }, [taxSearchTerm, taxDropdownOpen, dispatch]);

  useEffect(() => {
    if (costCenterDropdownOpen) {
      dispatch(
        getCostCenters({
          page: costCenterCurrentPage,
          per_page: 1000,
          search: costCenterSearchTerm || undefined,
        }),
      );
    }
  }, [
    costCenterCurrentPage,
    costCenterDropdownOpen,
    costCenterSearchTerm,
    dispatch,
  ]);

  useEffect(() => {
    if (costCenterSearchTerm !== undefined && costCenterDropdownOpen) {
      if (costCenterSearchTimeoutRef.current)
        clearTimeout(costCenterSearchTimeoutRef.current);
      costCenterSearchTimeoutRef.current = setTimeout(() => {
        setCostCenterCurrentPage(1);
        dispatch(
          getCostCenters({
            page: 1,
            per_page: 1000,
            search: costCenterSearchTerm || undefined,
          }),
        );
      }, 300);
    }
    return () => {
      if (costCenterSearchTimeoutRef.current)
        clearTimeout(costCenterSearchTimeoutRef.current);
    };
  }, [costCenterSearchTerm, costCenterDropdownOpen, dispatch]);

  // Close other dropdowns when opening one in project modal
  useEffect(() => {
    if (customerDropdownOpen) {
      setTaxDropdownOpen(false);
      setCostCenterDropdownOpen(false);
    }
  }, [customerDropdownOpen]);

  useEffect(() => {
    if (taxDropdownOpen) {
      setCustomerDropdownOpen(false);
      setCostCenterDropdownOpen(false);
    }
  }, [taxDropdownOpen]);

  useEffect(() => {
    if (costCenterDropdownOpen) {
      setCustomerDropdownOpen(false);
      setTaxDropdownOpen(false);
    }
  }, [costCenterDropdownOpen]);

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: isRTL });
      dispatch(clearSuccess());
    }
  }, [success, dispatch, isRTL]);

  useEffect(() => {
    if (error) {
      toast.error(error, { rtl: isRTL });
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch, isRTL]);

  // Handle auth success and error (for customer, tax, cost center modals)
  useEffect(() => {
    if (
      authSuccess &&
      !showAddCustomerModal &&
      !showAddTaxModal &&
      !showAddCostCenterModal
    ) {
      // Only show toast if modals are closed (to avoid duplicate toasts)
      toast.success(authSuccess, { rtl: isRTL });
      dispatch(clearAuthSuccess());
    }
  }, [
    authSuccess,
    showAddCustomerModal,
    showAddTaxModal,
    showAddCostCenterModal,
    dispatch,
    isRTL,
  ]);

  useEffect(() => {
    if (
      authError &&
      !showAddCustomerModal &&
      !showAddTaxModal &&
      !showAddCostCenterModal
    ) {
      // Only show toast if modals are closed (to avoid duplicate toasts)
      toast.error(authError, { rtl: isRTL });
      setTimeout(() => dispatch(clearAuthError()), 2500);
    }
  }, [
    authError,
    showAddCustomerModal,
    showAddTaxModal,
    showAddCostCenterModal,
    dispatch,
    isRTL,
  ]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (projectSearchTerm) {
        dispatch(getProjectsForReport({ search: projectSearchTerm, page: 1 }));
        setProjectPage(1);
      } else {
        dispatch(getProjectsForReport({ page: projectPage }));
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [projectSearchTerm, projectPage, dispatch]);

  useEffect(() => {
    if (projectDropdownOpen && !projectSearchTerm) {
      dispatch(getProjectsForReport({ page: projectPage }));
    }
  }, [projectDropdownOpen, projectSearchTerm, projectPage, dispatch]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (labSearchTerm) {
        dispatch(getLabTestsForReport({ search: labSearchTerm, page: 1 }));
        setLabPage(1);
      } else {
        dispatch(getLabTestsForReport({ page: labPage }));
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [labSearchTerm, labPage, dispatch]);

  useEffect(() => {
    if (labDropdownOpen && !labSearchTerm) {
      dispatch(getLabTestsForReport({ page: labPage }));
    }
  }, [labDropdownOpen, labSearchTerm, labPage, dispatch]);

  useEffect(() => {
    const handleClickOutside = e => {
      if (
        projectDropdownOpen &&
        !e.target.closest(".project-dropdown-container")
      )
        setProjectDropdownOpen(false);
      if (labDropdownOpen && !e.target.closest(".lab-dropdown-container"))
        setLabDropdownOpen(false);
      if (
        reportCustomerDropdownOpen &&
        !e.target.closest(".customer-report-dropdown-container")
      )
        setReportCustomerDropdownOpen(false);
      if (
        modalProjectDropdownOpen &&
        !e.target.closest(".modal-project-dropdown-container")
      )
        setModalProjectDropdownOpen(false);
      if (
        modalLabDropdownOpen &&
        !e.target.closest(".modal-lab-dropdown-container")
      )
        setModalLabDropdownOpen(false);
      // Close dropdowns in project modal when clicking outside
      if (showAddProjectModal) {
        if (
          customerDropdownOpen &&
          !e.target.closest("[data-dropdown-container]") &&
          !e.target.closest("[data-dropdown-toggle]")
        )
          setCustomerDropdownOpen(false);
        if (
          taxDropdownOpen &&
          !e.target.closest("[data-dropdown-container]") &&
          !e.target.closest("[data-dropdown-toggle]")
        )
          setTaxDropdownOpen(false);
        if (
          costCenterDropdownOpen &&
          !e.target.closest("[data-dropdown-container]") &&
          !e.target.closest("[data-dropdown-toggle]")
        )
          setCostCenterDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [
    projectDropdownOpen,
    labDropdownOpen,
    reportCustomerDropdownOpen,
    modalProjectDropdownOpen,
    modalLabDropdownOpen,
    showAddProjectModal,
    customerDropdownOpen,
    taxDropdownOpen,
    costCenterDropdownOpen,
  ]);

  // Reset modal dropdown pages when search term changes

  // عند تغيير البحث أو الصفحة في مودال المشروع: أرسل ريكوست جديد للـ API

  // حفظ meta للمشاريع
  useEffect(() => {
    setModalProjectPage(1);
  }, [modalProjectSearchTerm]);
  useEffect(() => {
    const fetchProjects = async () => {
      const res = await dispatch(
        getProjectsForReport({
          search: modalProjectSearchTerm || undefined,
          page: modalProjectPage,
          per_page: 10,
        }),
      );
      // جلب meta من الريسبونس
      const payload = res?.payload;
      const meta = payload?.data?.meta || payload?.meta || null;
      if (meta) setModalProjectMeta(meta);
      else
        setModalProjectMeta({
          current_page: modalProjectPage,
          last_page: 1,
          total: 0,
        });
    };
    fetchProjects();
  }, [modalProjectSearchTerm, modalProjectPage, dispatch]);

  // عند تغيير البحث أو الصفحة في مودال اختبار المعمل: أرسل ريكوست جديد للـ API

  // حفظ meta للاختبارات
  useEffect(() => {
    setModalLabPage(1);
  }, [modalLabSearchTerm]);
  useEffect(() => {
    const fetchLabTests = async () => {
      const res = await dispatch(
        getLabTestsForReport({
          search: modalLabSearchTerm || undefined,
          page: modalLabPage,
          per_page: 10,
        }),
      );
      // جلب meta من الريسبونس
      const payload = res?.payload;
      const meta = payload?.data?.meta || payload?.meta || null;
      if (meta) setModalLabMeta(meta);
      else
        setModalLabMeta({ current_page: modalLabPage, last_page: 1, total: 0 });
    };
    fetchLabTests();
  }, [modalLabSearchTerm, modalLabPage, dispatch]);

  // Auto-filter when projectId changes
  useEffect(() => {
    if (projectId) {
      fetchProjectTests({
        project_id: projectId,
        customer_id: reportCustomerId || undefined,
        lab_test_id: labTestId || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        page: 1,
      });
    }
  }, [projectId]);

  // Auto-filter when labTestId changes
  useEffect(() => {
    if (labTestId) {
      fetchProjectTests({
        project_id: projectId || undefined,
        customer_id: reportCustomerId || undefined,
        lab_test_id: labTestId,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        page: 1,
      });
    }
  }, [labTestId]);

  // Auto-filter when date range changes
  useEffect(() => {
    if (startDate || endDate) {
      fetchProjectTests({
        project_id: projectId || undefined,
        customer_id: reportCustomerId || undefined,
        lab_test_id: labTestId || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        page: 1,
      });
    }
  }, [startDate, endDate]);

  // Auto-filter when reportCustomerId changes
  useEffect(() => {
    if (reportCustomerId) {
      fetchProjectTests({
        project_id: projectId || undefined,
        customer_id: reportCustomerId,
        lab_test_id: labTestId || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        page: 1,
      });
    }
  }, [reportCustomerId]);

  const filteredProjects = useMemo(() => {
    if (!Array.isArray(projects)) return [];
    if (!projectSearchTerm) return projects;
    const term = projectSearchTerm.toLowerCase();
    return projects.filter(p => {
      const nameAr = p.name_ar?.toLowerCase() || "";
      const nameEn = p.name?.toLowerCase() || "";
      const nameEnExplicit = p.name_en?.toLowerCase() || "";
      const code = p.code?.toLowerCase() || "";
      return (
        nameAr.includes(term) ||
        nameEn.includes(term) ||
        nameEnExplicit.includes(term) ||
        code.includes(term)
      );
    });
  }, [projects, projectSearchTerm]);

  const filteredLabTests = useMemo(() => {
    if (!Array.isArray(labTests)) return [];
    if (!labSearchTerm) return labTests;
    const term = labSearchTerm.toLowerCase();
    return labTests.filter(tst => {
      const nameAr = tst.name_ar?.toLowerCase() || "";
      const nameEn = tst.name?.toLowerCase() || "";
      const nameEnExplicit = tst.name_en?.toLowerCase() || "";
      const code = tst.code?.toLowerCase() || "";
      return (
        nameAr.includes(term) ||
        nameEn.includes(term) ||
        nameEnExplicit.includes(term) ||
        code.includes(term)
      );
    });
  }, [labTests, labSearchTerm]);

  const filteredCustomersReport = useMemo(() => {
    if (!Array.isArray(customers)) return [];
    if (!reportCustomerSearchTerm) return customers;
    const term = reportCustomerSearchTerm.toLowerCase();
    return customers.filter(c => {
      const nameAr = c.name_ar?.toLowerCase() || "";
      const nameEn = c.name_en?.toLowerCase() || "";
      const name = c.name?.toLowerCase() || "";
      const code = c.code?.toLowerCase() || "";
      return (
        nameAr.includes(term) ||
        nameEn.includes(term) ||
        name.includes(term) ||
        code.includes(term)
      );
    });
  }, [customers, reportCustomerSearchTerm]);

  // باجينيشن 10 عناصر فقط في كل صفحة

  // لا تقسيم محلي: فقط اعرض المشاريع/الاختبارات كما هي من الـ API (هي بالفعل صفحة واحدة فقط)
  const filteredModalProjects = useMemo(() => {
    if (!Array.isArray(projects)) return [];
    return projects;
  }, [projects]);

  const filteredModalLabTests = useMemo(() => {
    if (!Array.isArray(labTests)) return [];
    return labTests;
  }, [labTests]);

  const ITEMS_PER_PAGE = 10;
  const projectMaxPage = useMemo(() => {
    const total = filteredProjects.length;
    return Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));
  }, [filteredProjects]);

  const labTestMaxPage = useMemo(() => {
    const total = filteredLabTests.length;
    return Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));
  }, [filteredLabTests]);

  // استخدم meta من الريسبونس
  const modalProjectMaxPage = useMemo(() => {
    return modalProjectMeta?.last_page || 1;
  }, [modalProjectMeta]);

  const modalLabTestMaxPage = useMemo(() => {
    return modalLabMeta?.last_page || 1;
  }, [modalLabMeta]);

  const fetchProjectTests = async (params = {}) => {
    try {
      setIsLoading(true);
      const token = getToken();
      const currentLang = getCurrentLanguage();
      const response = await axiosInstance.get(`${BASE_URL}/project-tests`, {
        params,
      });
      // Handle nested response structure
      const responseData = response.data?.data || response.data || {};
      const projectTestsData = Array.isArray(responseData.data)
        ? responseData.data
        : Array.isArray(responseData)
          ? responseData
          : [];
      const meta = responseData.meta || response.data?.meta || null;

      setProjectTests(projectTestsData);
      setMeta(meta);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err.message || "فشل تحميل البيانات",
        { rtl: isRTL },
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    fetchProjectTests({
      project_id: projectId,
      customer_id: reportCustomerId,
      lab_test_id: labTestId,
      start_date: startDate,
      end_date: endDate,
      page: 1,
    });
  };

  const handleCreateSave = async () => {
    if (!modalProjectId || !modalLabTestId) {
      toast.error(t("projectReports.selectProject"), { rtl: isRTL });
      return;
    }
    try {
      setIsSaving(true);
      const token = getToken();
      const currentLang = getCurrentLanguage();
      const payload = {
        project_id: Number(modalProjectId),
        lab_test_id: Number(modalLabTestId),
        test_date: modalTestDate,
        qty: Number(modalQty),
        description: modalDescription || "—",
      };

      await axiosInstance.post(`${BASE_URL}/project-tests`, payload);
      toast.success(t("projectReports.saved"), { rtl: isRTL });

      setModalMode(null);
      setEditingId(null);

      // Fetch with current filters if they exist
      const filters = {};
      if (projectId) filters.project_id = projectId;
      if (labTestId) filters.lab_test_id = labTestId;
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
      filters.page = 1;

      fetchProjectTests(filters);
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || t("projectReports.errors.saveError"), {
        rtl: isRTL,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditSave = async () => {
    if (!modalProjectId || !modalLabTestId) {
      toast.error(t("projectReports.selectProject"), { rtl: isRTL });
      return;
    }
    try {
      setIsSaving(true);
      const token = getToken();
      const currentLang = getCurrentLanguage();
      const payload = {
        project_id: Number(modalProjectId),
        lab_test_id: Number(modalLabTestId),
        test_date: modalTestDate,
        qty: Number(modalQty),
        description: modalDescription || "—",
      };

      await axiosInstance.put(
        `${BASE_URL}/project-tests/${editingId}`,
        payload,
      );
      toast.success(t("projectReports.updated"), { rtl: isRTL });

      setEditingId(null);
      setModalMode(null);

      // Fetch with current filters if they exist
      const filters = {};
      if (projectId) filters.project_id = projectId;
      if (labTestId) filters.lab_test_id = labTestId;
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
      filters.page = 1;

      fetchProjectTests(filters);
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || t("projectReports.errors.saveError"), {
        rtl: isRTL,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async id => {
    if (!id) return;
    // Store backup before optimistic delete
    const testToDelete = projectTests.find(test => test.id === id);

    try {
      // Optimistically remove from local state first
      setProjectTests(prev => prev.filter(test => test.id !== id));
      setIsDeleting(true);
      await axiosInstance.delete(`${BASE_URL}/project-tests/${id}`);
      toast.success(t("projectReports.deleted"), { rtl: isRTL });
      // No need to fetch - already optimistically updated
    } catch (err) {
      // Revert optimistic update on error
      if (testToDelete) {
        setProjectTests(prev => {
          const exists = prev.find(test => test.id === testToDelete.id);
          if (!exists) {
            return [...prev, testToDelete].sort((a, b) => {
              if (a.id && b.id) return a.id - b.id;
              return 0;
            });
          }
          return prev;
        });
      }
      toast.error(err?.response?.data?.message || err.message || t("projectReports.errors.deleteError"), {
        rtl: isRTL,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddProject = async e => {
    e.preventDefault();
    if (!projectForm.name_ar) {
      toast.error(t("common.fillRequiredFields"), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmittingProject(true);
      const token = getToken();
      const currentLang = getCurrentLanguage();
      const payload = {
        code: projectForm.code,
        name_ar: projectForm.name_ar,
        name_en: projectForm.name_en || undefined,
        customer_id: projectForm.customer_id || null,
        start_date: projectForm.start_date || undefined,
        end_date: projectForm.end_date || undefined,
        status: projectForm.status,
        contract_value: projectForm.contract_value
          ? Number(projectForm.contract_value)
          : undefined,
        tax_id: projectForm.tax_id || null,
        cost_center_id: projectForm.cost_center_id || null,
        notes: projectForm.notes || undefined,
      };
      const response = await axiosInstance.post(
        `${BASE_URL}/projects`,
        payload,
      );
      const newProject = response.data?.data || response.data;
      if (newProject?.id) {
        // Refresh projects list
        dispatch(getProjectsForReport({ per_page: 1000 }));
        // Set the newly added project in modal
        setModalProjectId(String(newProject.id));
        // Close modal and reset form
        setShowAddProjectModal(false);
        setProjectForm({
          code: "",
          name_ar: "",
          name_en: "",
          customer_id: null,
          start_date: "",
          end_date: "",
          status: "planned",
          contract_value: "",
          tax_id: null,
          cost_center_id: null,
          notes: "",
        });
        toast.success(
          t("projectReports.projectAdded", {
            defaultValue: "تم إضافة المشروع بنجاح",
          }),
          { rtl: isRTL },
        );
      }
    } catch (err) {
      const errorData = err?.response?.data || err;
      let errorMessage = t("projectReports.projectAddError", {
        defaultValue: "فشل إضافة المشروع",
      });

      if (typeof errorData === "string") {
        errorMessage = errorData;
      } else if (errorData?.errors) {
        const errorMessages = Object.values(errorData.errors).flat();
        errorMessage = errorMessages.join(", ");
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (err?.message) {
        errorMessage = err.message;
      }
      toast.error(errorMessage, { rtl: isRTL });
    } finally {
      setIsSubmittingProject(false);
    }
  };

  const handleAddTest = async e => {
    e.preventDefault();
    if (!testForm.name_ar || !testForm.name_en) {
      toast.error(
        t("tasks.add.messages.testNameRequired", {
          defaultValue: "برجاء إدخال اسم الاختبار",
        }),
        { rtl: isRTL },
      );
      return;
    }
    try {
      setIsSubmittingTest(true);
      const token = getToken();
      const currentLang = getCurrentLanguage();
      const response = await axiosInstance.post(`${BASE_URL}/tests`, {
        name_ar: testForm.name_ar,
        name_en: testForm.name_en,
        is_active: testForm.is_active,
      });
      const newTest = response.data?.data || response.data;
      if (newTest?.id) {
        // Refresh lab tests list
        dispatch(getLabTestsForReport({ per_page: 1000 }));
        // Set the newly added test in modal
        setModalLabTestId(String(newTest.id));
        // Close modal and reset form
        setShowAddTestModal(false);
        setTestForm({
          name_ar: "",
          name_en: "",
          is_active: true,
        });
        // Use API response message if available, otherwise use default message
        const successMessage =
          response.data?.message ||
          response.data?.data?.message ||
          t("tasks.add.messages.testAdded", {
            defaultValue: "تم إضافة الاختبار بنجاح",
          });
        toast.success(successMessage, { rtl: isRTL });
      }
    } catch (err) {
      const errorData = err?.response?.data || err;
      let errorMessage = t("tasks.add.messages.testAddError", {
        defaultValue: "فشل إضافة الاختبار",
      });

      if (typeof errorData === "string") {
        errorMessage = errorData;
      } else if (errorData?.errors) {
        const errorMessages = Object.values(errorData.errors).flat();
        errorMessage = errorMessages.join(", ");
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (err?.message) {
        errorMessage = err.message;
      }
      toast.error(errorMessage, { rtl: isRTL });
    } finally {
      setIsSubmittingTest(false);
    }
  };

  const handleProjectFormChange = e => {
    const { name, value } = e.target;
    setProjectForm(prev => ({ ...prev, [name]: value }));
  };

  const handleTaxFormChange = e => {
    const { name, value, type, checked } = e.target;
    setTaxForm(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCostCenterFormChange = e => {
    const { name, value } = e.target;
    setCostCenterForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCustomerFormChange = e => {
    const { name, value, type, checked } = e.target;
    setCustomerForm(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAddCustomer = async e => {
    e.preventDefault();
    if (!customerForm.name_ar || !customerForm.name_en) {
      toast.error(
        t("quotations.add.modals.addCustomer.requiredFields", {
          defaultValue: "برجاء إدخال الاسم بالعربية والإنجليزية",
        }),
        { rtl: isRTL },
      );
      return;
    }
    try {
      setIsSubmittingCustomer(true);
      const submitData = {
        ...customerForm,
        is_active: customerForm.is_active ? true : false,
      };
      const result = await dispatch(addCustomer(submitData)).unwrap();
      if (result?.data?.id) {
        // Clear any success/error from Redux state
        dispatch(clearAuthSuccess());
        dispatch(clearAuthError());
        // Set the newly added customer in project form
        setProjectForm(prev => ({ ...prev, customer_id: result.data.id }));
        // Refresh customers list
        await dispatch(
          getCustomers({
            page: customerCurrentPage,
            per_page: 1000,
            search: customerSearchTerm || undefined,
          }),
        );
        setShowAddCustomerModal(false);
        setCustomerForm({
          name_en: "",
          name_ar: "",
          code: "",
          contact_person: "",
          email: "",
          phone: "",
          address: "",
          tax_number: "",
          is_active: true,
          notes: "",
        });
        toast.success(
          t("quotations.add.modals.addCustomer.success", {
            defaultValue: "تم إضافة العميل بنجاح",
          }),
          { rtl: isRTL },
        );
      }
    } catch (error) {
      // Clear error from Redux state to prevent it from showing later
      dispatch(clearAuthError());
      const errorData = error?.response?.data || error;
      let errorMessage = t("quotations.add.modals.addCustomer.error", {
        defaultValue: "فشل إضافة العميل",
      });

      if (typeof errorData === "string") {
        errorMessage = errorData;
      } else if (errorData?.errors) {
        const errorMessages = Object.values(errorData.errors).flat();
        errorMessage = errorMessages.join(", ");
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage, { rtl: isRTL });
    } finally {
      setIsSubmittingCustomer(false);
    }
  };

  const handleAddTax = async e => {
    e.preventDefault();
    if (!taxForm.name_ar || !taxForm.name_en || taxForm.rate === "") {
      toast.error(t("common.fillRequiredFields"), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmittingTax(true);
      const result = await dispatch(
        addTax({
          name_ar: taxForm.name_ar,
          name_en: taxForm.name_en,
          rate: Number(taxForm.rate),
          is_active: Boolean(taxForm.is_active),
        }),
      ).unwrap();
      if (result?.data?.id) {
        // Clear any success/error from Redux state
        dispatch(clearAuthSuccess());
        dispatch(clearAuthError());
        // Set the newly added tax in project form
        setProjectForm(prev => ({ ...prev, tax_id: result.data.id }));
        // Refresh taxes list
        await dispatch(
          getTaxes({
            page: taxCurrentPage,
            per_page: 1000,
            search: taxSearchTerm || undefined,
          }),
        );
        setShowAddTaxModal(false);
        setTaxForm({
          name_ar: "",
          name_en: "",
          rate: "",
          is_active: true,
        });
        toast.success(
          t("projectReports.taxAdded", {
            defaultValue: "تم إضافة الضريبة بنجاح",
          }),
          { rtl: isRTL },
        );
      }
    } catch (error) {
      // Clear error from Redux state to prevent it from showing later
      dispatch(clearAuthError());
      const errorData = error?.response?.data || error;
      let errorMessage = t("projectReports.taxAddError", {
        defaultValue: "فشل إضافة الضريبة",
      });

      if (typeof errorData === "string") {
        errorMessage = errorData;
      } else if (errorData?.errors) {
        const errorMessages = Object.values(errorData.errors).flat();
        errorMessage = errorMessages.join(", ");
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage, { rtl: isRTL });
    } finally {
      setIsSubmittingTax(false);
    }
  };

  const handleAddCostCenter = async e => {
    e.preventDefault();
    if (!costCenterForm.name_ar || !costCenterForm.name_en) {
      toast.error(t("common.fillRequiredFields"), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmittingCostCenter(true);
      const result = await dispatch(
        addCostCenter({
          name_ar: costCenterForm.name_ar,
          name_en: costCenterForm.name_en,
          code: costCenterForm.code || undefined,
        }),
      ).unwrap();
      if (result?.data?.id) {
        // Clear any success/error from Redux state
        dispatch(clearAuthSuccess());
        dispatch(clearAuthError());
        // Set the newly added cost center in project form
        setProjectForm(prev => ({ ...prev, cost_center_id: result.data.id }));
        // Refresh cost centers list
        await dispatch(
          getCostCenters({
            page: costCenterCurrentPage,
            per_page: 1000,
            search: costCenterSearchTerm || undefined,
          }),
        );
        setShowAddCostCenterModal(false);
        setCostCenterForm({
          name_ar: "",
          name_en: "",
          code: "",
        });
        toast.success(
          t("projectReports.costCenterAdded", {
            defaultValue: "تم إضافة مركز التكلفة بنجاح",
          }),
          { rtl: isRTL },
        );
      }
    } catch (error) {
      // Clear error from Redux state to prevent it from showing later
      dispatch(clearAuthError());
      const errorData = error?.response?.data || error;
      let errorMessage = t("projectReports.costCenterAddError", {
        defaultValue: "فشل إضافة مركز التكلفة",
      });

      if (typeof errorData === "string") {
        errorMessage = errorData;
      } else if (errorData?.errors) {
        const errorMessages = Object.values(errorData.errors).flat();
        errorMessage = errorMessages.join(", ");
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage, { rtl: isRTL });
    } finally {
      setIsSubmittingCostCenter(false);
    }
  };

  const handlePreview = (type = "all", row = null) => {
    const hasData =
      type === "all" ? projectTests && projectTests.length > 0 : !!row;
    if (!hasData) {
      toast.error(t("projectReports.noData"), { rtl: isRTL });
      return;
    }
    setSelectedTest(row);
    setPreviewType(type);
    setIsPreviewOpen(true);
  };

  const handlePreviewAll = () => {
    if (!projectTests || projectTests.length === 0) {
      toast.error(t("projectReports.noData"), { rtl: isRTL });
      return;
    }
    setPreviewType("all");
    setSelectedTest(null);
    setIsPreviewOpen(true);
  };

  const handleOpenCreateModal = () => {
    setModalMode("create");
    setEditingId(null);
    setModalProjectId(projectId || "");
    setModalLabTestId(labTestId || "");
    setModalTestDate("");
    setModalQty(1);
    setModalDescription("");
    setModalProjectPage(1);
    setModalLabPage(1);
    setModalProjectSearchTerm("");
    setModalLabSearchTerm("");
  };

  const handleResetFilters = async () => {
    setProjectId("");
    setLabTestId("");
    setStartDate("");
    setEndDate("");
    setReportCustomerId("");
    setProjectSearchTerm("");
    setLabSearchTerm("");
    setReportCustomerSearchTerm("");
    setProjectPage(1);
    setLabPage(1);
    setProjectDropdownOpen(false);
    setLabDropdownOpen(false);
    setReportCustomerDropdownOpen(false);
    setSelectedTest(null);
    setPreviewType(null);
    setIsPreviewOpen(false);

    // Fetch all data without filters
    await fetchProjectTests({ page: 1 });
  };

  const handlePrintAll = async () => {
    try {
      setIsGeneratingPdf(true);

      // Get current data to extract total
      const total = meta?.total || projectTests.length;

      if (total === 0 || projectTests.length === 0) {
        toast.error(t("projectReports.noData"), { rtl: isRTL });
        setIsGeneratingPdf(false);
        return;
      }

      // Fetch all project tests with per_page = total
      const params = {
        page: 1,
        per_page: total,
        project_id: projectId || undefined,
        customer_id: reportCustomerId || undefined,
        lab_test_id: labTestId || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      };

      const token = getToken();
      const currentLang = getCurrentLanguage();
      const response = await axiosInstance.get(`${BASE_URL}/project-tests`, {
        params,
      });

      const responseData = response.data?.data || response.data || {};
      const allProjectTests = Array.isArray(responseData.data)
        ? responseData.data
        : Array.isArray(responseData)
          ? responseData
          : [];

      if (!allProjectTests || allProjectTests.length === 0) {
        toast.error(t("projectReports.noData"), { rtl: isRTL });
        setIsGeneratingPdf(false);
        return;
      }

      // Split data into chunks of 20 items per page
      const chunks = chunkArray(allProjectTests, 20);
      setPdfChunks(chunks);

      setPdfType("all");
      await new Promise(resolve => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise(resolve => setTimeout(resolve, 200));

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.min(2, Math.max(1.5, devicePixelRatio));

      // Generate PDF for each chunk
      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        // Wait for DOM update
        await new Promise(resolve => requestAnimationFrame(resolve));

        const element = document.getElementById(
          `project-tests-pdf-all-${chunkIndex}`,
        );
        if (!element) {
          if (chunkIndex === 0) {
            setPdfChunks([]);
            toast.error(t("projectReports.printError"), { rtl: isRTL });
            setIsGeneratingPdf(false);
            setPdfType(null);
            return;
          }
          continue;
        }

        const canvas = await html2canvas(element, {
          scale,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          letterRendering: false,
          allowTaint: false,
          removeContainer: false,
          imageTimeout: 10000,
          onclone: clonedDoc => {
            const clonedElement = clonedDoc.getElementById(
              `project-tests-pdf-all-${chunkIndex}`,
            );
            if (clonedElement) {
              clonedElement.style.fontFamily =
                '"Times New Roman Bold", "Times New Roman", "Times", serif';
              clonedElement.style.fontWeight = "bold";
              clonedElement.style.direction = isRTL ? "rtl" : "ltr";
            }
          },
        });

        const imgData = canvas.toDataURL("image/png", 1.0);
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;

        if (chunkIndex > 0) {
          pdf.addPage();
        }

        // Add the image to the current page
        pdf.addImage(
          imgData,
          "PNG",
          0,
          0,
          pdfWidth,
          imgHeight,
          undefined,
          "FAST",
        );
      }

      pdf.save(
        `ACE_ProjectTests_${new Date().toISOString().split("T")[0]}.pdf`,
      );
      toast.success(
        t("projectReports.printed", { defaultValue: "تم الطباعة بنجاح" }),
        { rtl: isRTL },
      );

      // Restore
      setPdfChunks([]);
    } catch (error) {
      console.error("Project Tests PDF error:", error);
      toast.error(t("projectReports.printError"), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
    }
  };

  const handlePrintSingle = async row => {
    if (!row) {
      toast.error(t("projectReports.noData"), { rtl: isRTL });
      return;
    }

    try {
      setSelectedTest(row);
      setPdfType("single");
      await new Promise(resolve => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      setIsGeneratingPdf(true);

      const elements = [
        document.getElementById("project-tests-pdf-single") ||
          document.getElementById("preview-project-tests-pdf-single"),
      ].filter(Boolean);

      if (elements.length === 0) {
        toast.error(t("projectReports.printError"), { rtl: isRTL });
        return;
      }

      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.min(2, Math.max(1.5, devicePixelRatio));
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      for (let i = 0; i < elements.length; i++) {
        if (i > 0) pdf.addPage();

        const canvas = await html2canvas(elements[i], {
          scale,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          letterRendering: false,
          allowTaint: false,
          removeContainer: false,
          imageTimeout: 10000,
          onclone: clonedDoc => {
            const clonedElement = clonedDoc.getElementById(
              "preview-project-tests-pdf-single",
            );
            if (clonedElement) {
              clonedElement.style.fontFamily =
                '"Times New Roman Bold", "Times New Roman", "Times", serif';
              clonedElement.style.fontWeight = "bold";
              clonedElement.style.direction = isRTL ? "rtl" : "ltr";
            }
          },
        });

        const imgData = canvas.toDataURL("image/png", 1.0);
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;
        // Add the image to the current page. Since the template has a fixed height (1123px),
        // it should align perfectly with the PDF page height.
        pdf.addImage(
          imgData,
          "PNG",
          0,
          0,
          pdfWidth,
          imgHeight,
          undefined,
          "FAST",
        );
      }

      const projectName = isRTL
        ? row.project?.name_ar || row.project?.name_en || row.project?.name
        : row.project?.name_en || row.project?.name_ar || row.project?.name;
      const fileName = `ACE_ProjectTest_${projectName || row.id}_${new Date().toISOString().split("T")[0]}.pdf`;
      pdf.save(fileName);
      toast.success(
        t("projectReports.printed", { defaultValue: "تم الطباعة بنجاح" }),
        { rtl: isRTL },
      );
    } catch (error) {
      console.error("Project Test PDF error:", error);
      toast.error(t("projectReports.printError"), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setSelectedTest(null);
    }
  };

  const handleExportToExcel = rows => {
    if (!rows || rows.length === 0) {
      toast.error(t("projectReports.noData"), { rtl: isRTL });
      return;
    }
    try {
      const excelData = [
        {
          [t("projectReports.period")]:
            `${formatDate(startDate)} - ${formatDate(endDate)}`,
        },
        {},
        ...rows.map(row => ({
          [t("projectReports.project")]: isRTL
            ? row.project?.name_ar || row.project?.name_en || row.project?.name
            : row.project?.name_en || row.project?.name_ar || row.project?.name,
          [t("projectReports.customer", { defaultValue: "العميل" })]: isRTL
            ? row.project?.customer?.name_ar ||
              row.project?.customer?.name_en ||
              row.project?.customer?.name ||
              "—"
            : row.project?.customer?.name_en ||
              row.project?.customer?.name_ar ||
              row.project?.customer?.name ||
              "—",
          [t("projectReports.labTest")]: isRTL
            ? row.lab_test?.name_ar || row.lab_test?.name
            : row.lab_test?.name || row.lab_test?.name_ar,
          [t("projectReports.testDate")]: formatDate(row.test_date),
          [t("projectReports.qty")]: row.qty ?? 0,
          [t("projectReports.description")]: row.description || "—",
        })),
      ];
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Project Tests");
      const filename =
        rows.length === 1
          ? `ProjectTest_${rows[0]?.id || "item"}_${new Date().toISOString().split("T")[0]}.xlsx`
          : `ProjectTests_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(workbook, filename);
      toast.success(t("projectReports.exported"), { rtl: isRTL });
    } catch (err) {
      toast.error(err.message || t("projectReports.exportError"), {
        rtl: isRTL,
      });
    }
  };

  const formatNumber = value =>
    Number(value ?? 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatDate = dateString => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const getProjectName = project => {
    if (!project) return "";
    return isRTL
      ? project.name_ar || project.name_en || project.name || ""
      : project.name_en || project.name || project.name_ar || "";
  };

  const getLabTestName = labTest => {
    if (!labTest) return "";
    return isRTL
      ? labTest.name_ar || labTest.name_en || labTest.name || ""
      : labTest.name_en || labTest.name || labTest.name_ar || "";
  };

  const getCustomerName = customer => {
    if (!customer) return "";
    return isRTL
      ? customer.name_ar || customer.name_en || customer.name || ""
      : customer.name_en || customer.name || customer.name_ar || "";
  };

  const selectedProject = Array.isArray(projects)
    ? projects.find(p => String(p.id) === String(projectId))
    : null;
  const selectedLabTest = Array.isArray(labTests)
    ? labTests.find(tst => String(tst.id) === String(labTestId))
    : null;
  const selectedReportCustomer = Array.isArray(customers)
    ? customers.find(c => String(c.id) === String(reportCustomerId))
    : null;
  const isEntryModalOpen = modalMode !== null;

  // Helper function to render dropdown items
  const renderDropdownItem = (item, codeField, nameArField, nameEnField) => {
    const code = item[codeField] || "";
    const name = isRTL
      ? item[nameArField] || item[nameEnField] || ""
      : item[nameEnField] || item[nameArField] || "";
    return `${code ? `${code} - ` : ""}${name}`;
  };

  // DropdownWithSearch is now defined outside this component (above the ProjectTests declaration)

  return (
    <div style={{ padding: "20px", direction: isRTL ? "rtl" : "ltr" }}>
      <div
        style={{
          backgroundColor: "#1b3350",
          borderRadius: "12px",
          padding: "24px",
          border: "1px solid #333",
        }}
      >
        <h2
          style={{
            color: "white",
            marginBottom: "20px",
            textAlign: isRTL ? "right" : "left",
          }}
        >
          {t("projectReports.projectTests")}
        </h2>

        <form
          onSubmit={handleSubmit}
          style={{
            backgroundColor: "#1a1f2e",
            padding: "20px",
            borderRadius: "8px",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "15px",
              marginBottom: "15px",
            }}
          >
            {/* Project dropdown */}
            <div
              style={{ position: "relative" }}
              className="project-dropdown-container"
            >
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: labelAlign,
                }}
              >
                {t("projectReports.project")}
              </label>
              <div
                onClick={() => {
                  setProjectDropdownOpen(!projectDropdownOpen);
                  if (!projectDropdownOpen) {
                    setTimeout(() => {
                      if (projectSearchInputRef.current)
                        projectSearchInputRef.current.focus();
                    }, 100);
                  }
                }}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    flex: 1,
                    textAlign: labelAlign,
                    direction: isRTL ? "rtl" : "ltr",
                  }}
                >
                  {selectedProject
                    ? getProjectName(selectedProject) || `#${projectId}`
                    : t("projectReports.selectProject")}
                </span>
                <span>▼</span>
              </div>
              {projectDropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "#14263b",
                    border: "1px solid #333",
                    borderRadius: "6px",
                    marginTop: "4px",
                    maxHeight: "400px",
                    overflowY: "auto",
                    zIndex: 1000,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                  }}
                >
                  <div
                    style={{ padding: "8px", borderBottom: "1px solid #333" }}
                  >
                    <input
                      ref={projectSearchInputRef}
                      type="text"
                      placeholder={t("common.search", {
                        defaultValue: isRTL ? "بحث..." : "Search...",
                      })}
                      value={projectSearchTerm}
                      onChange={e => setProjectSearchTerm(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{
                        width: "100%",
                        padding: "8px",
                        backgroundColor: "#0d1117",
                        border: "none",
                        borderBottom: "1px solid #333",
                        color: "white",
                        borderRadius: "6px 6px 0 0",
                        textAlign: labelAlign,
                        direction: isRTL ? "rtl" : "ltr",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      padding: "8px",
                      borderTop: "1px solid #333",
                      background: "#14263b",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "4px",
                        marginBottom: "6px",
                      }}
                    >
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          setProjectPage(1);
                        }}
                        disabled={projectPage === 1}
                        style={{
                          padding: "4px 8px",
                          backgroundColor:
                            projectPage === 1 ? "#2a2a2a" : "#1a1f2e",
                          color: projectPage === 1 ? "#666" : "white",
                          border: "1px solid #444",
                          borderRadius: "4px",
                          cursor: projectPage === 1 ? "not-allowed" : "pointer",
                          fontSize: "10px",
                          fontWeight: "bold",
                          minWidth: "28px",
                        }}
                      >
                        ««
                      </button>
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          setProjectPage(prev => Math.max(1, prev - 1));
                        }}
                        disabled={projectPage === 1}
                        style={{
                          padding: "4px 8px",
                          backgroundColor:
                            projectPage === 1 ? "#2a2a2a" : "#1a1f2e",
                          color: projectPage === 1 ? "#666" : "white",
                          border: "1px solid #444",
                          borderRadius: "4px",
                          cursor: projectPage === 1 ? "not-allowed" : "pointer",
                          fontSize: "10px",
                          fontWeight: "bold",
                          minWidth: "28px",
                        }}
                      >
                        ‹
                      </button>
                      <span
                        style={{
                          color: "#ccc",
                          fontSize: "11px",
                          padding: "0 8px",
                        }}
                      >
                        {t("projectReports.page")} {projectPage}
                      </span>
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          setProjectPage(prev =>
                            Math.min(prev + 1, projectMaxPage),
                          );
                        }}
                        disabled={projectPage >= projectMaxPage}
                        style={{
                          padding: "4px 8px",
                          backgroundColor: "#1a1f2e",
                          color:
                            projectPage >= projectMaxPage ? "#666" : "white",
                          border: "1px solid #444",
                          borderRadius: "4px",
                          cursor:
                            projectPage >= projectMaxPage
                              ? "not-allowed"
                              : "pointer",
                          fontSize: "10px",
                          fontWeight: "bold",
                          minWidth: "28px",
                          opacity: projectPage >= projectMaxPage ? 0.5 : 1,
                        }}
                      >
                        ›
                      </button>
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          setProjectPage(projectMaxPage);
                        }}
                        disabled={projectPage >= projectMaxPage}
                        style={{
                          padding: "4px 8px",
                          backgroundColor:
                            projectPage >= projectMaxPage
                              ? "#2a2a2a"
                              : "#1a1f2e",
                          color:
                            projectPage >= projectMaxPage ? "#666" : "white",
                          border: "1px solid #444",
                          borderRadius: "4px",
                          cursor:
                            projectPage >= projectMaxPage
                              ? "not-allowed"
                              : "pointer",
                          fontSize: "10px",
                          fontWeight: "bold",
                          minWidth: "28px",
                        }}
                      >
                        ››
                      </button>
                    </div>
                  </div>
                  <div style={{ maxHeight: "260px", overflowY: "auto" }}>
                    {filteredProjects.length === 0 ? (
                      <div
                        style={{
                          padding: "20px",
                          textAlign: "center",
                          color: "#ccc",
                        }}
                      >
                        {t("projectReports.noData")}
                      </div>
                    ) : (
                      filteredProjects.map(p => (
                        <div
                          key={p.id}
                          onClick={() => {
                            setProjectId(String(p.id));
                            setProjectDropdownOpen(false);
                            setProjectSearchTerm("");
                          }}
                          style={{
                            padding: "10px 12px",
                            cursor: "pointer",
                            borderTop: "1px solid #333",
                            color: "white",
                            backgroundColor:
                              String(projectId) === String(p.id)
                                ? "#2a2a2a"
                                : "transparent",
                            transition: "background-color 0.2s",
                            textAlign: labelAlign,
                            direction: isRTL ? "rtl" : "ltr",
                          }}
                        >
                          {p.code ? `${p.code} - ` : ""}
                          {getProjectName(p) || `#${p.id}`}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Customer dropdown (report filter) */}
            <div
              style={{ position: "relative" }}
              className="customer-report-dropdown-container"
            >
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: labelAlign,
                }}
              >
                {t("projectReports.customer", { defaultValue: "العميل" })}
              </label>
              <div
                onClick={() => {
                  setReportCustomerDropdownOpen(!reportCustomerDropdownOpen);
                  if (!reportCustomerDropdownOpen) {
                    setTimeout(() => {
                      if (reportCustomerSearchInputRef.current)
                        reportCustomerSearchInputRef.current.focus();
                    }, 100);
                  }
                }}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    flex: 1,
                    textAlign: labelAlign,
                    direction: isRTL ? "rtl" : "ltr",
                  }}
                >
                  {selectedReportCustomer
                    ? getCustomerName(selectedReportCustomer) ||
                      `#${reportCustomerId}`
                    : t("projectReports.selectCustomer", {
                        defaultValue: "اختر العميل",
                      })}
                </span>
                {reportCustomerId ? (
                  <span
                    onClick={e => {
                      e.stopPropagation();
                      setReportCustomerId("");
                      setReportCustomerSearchTerm("");
                      setReportCustomerDropdownOpen(false);
                    }}
                    style={{ padding: "0 4px" }}
                  >
                    <FaTimes />
                  </span>
                ) : (
                  <span>▼</span>
                )}
              </div>
              {reportCustomerDropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "#14263b",
                    border: "1px solid #333",
                    borderRadius: "6px",
                    marginTop: "4px",
                    maxHeight: "400px",
                    overflowY: "auto",
                    zIndex: 1000,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                  }}
                >
                  <div
                    style={{ padding: "8px", borderBottom: "1px solid #333" }}
                  >
                    <input
                      ref={reportCustomerSearchInputRef}
                      type="text"
                      placeholder={t("common.search", {
                        defaultValue: isRTL ? "بحث..." : "Search...",
                      })}
                      value={reportCustomerSearchTerm}
                      onChange={e =>
                        setReportCustomerSearchTerm(e.target.value)
                      }
                      onClick={e => e.stopPropagation()}
                      style={{
                        width: "100%",
                        padding: "8px",
                        backgroundColor: "#0d1117",
                        border: "none",
                        borderBottom: "1px solid #333",
                        color: "white",
                        borderRadius: "6px 6px 0 0",
                        textAlign: labelAlign,
                        direction: isRTL ? "rtl" : "ltr",
                      }}
                    />
                  </div>
                  <div style={{ maxHeight: "260px", overflowY: "auto" }}>
                    {filteredCustomersReport.length === 0 ? (
                      <div
                        style={{
                          padding: "20px",
                          textAlign: "center",
                          color: "#ccc",
                        }}
                      >
                        {t("projectReports.noData")}
                      </div>
                    ) : (
                      filteredCustomersReport.map(c => (
                        <div
                          key={c.id}
                          onClick={() => {
                            setReportCustomerId(String(c.id));
                            setReportCustomerDropdownOpen(false);
                            setReportCustomerSearchTerm("");
                          }}
                          style={{
                            padding: "10px 12px",
                            cursor: "pointer",
                            borderTop: "1px solid #333",
                            color: "white",
                            backgroundColor:
                              String(reportCustomerId) === String(c.id)
                                ? "#2a2a2a"
                                : "transparent",
                            transition: "background-color 0.2s",
                            textAlign: labelAlign,
                            direction: isRTL ? "rtl" : "ltr",
                          }}
                        >
                          {c.code ? `${c.code} - ` : ""}
                          {getCustomerName(c) || `#${c.id}`}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Lab tests dropdown */}
            <div
              style={{ position: "relative" }}
              className="lab-dropdown-container"
            >
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: labelAlign,
                }}
              >
                {t("projectReports.labTest")}
              </label>
              <div
                onClick={() => {
                  setLabDropdownOpen(!labDropdownOpen);
                  if (!labDropdownOpen) {
                    setTimeout(() => {
                      if (labSearchInputRef.current)
                        labSearchInputRef.current.focus();
                    }, 100);
                  }
                }}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    flex: 1,
                    textAlign: labelAlign,
                    direction: isRTL ? "rtl" : "ltr",
                  }}
                >
                  {selectedLabTest
                    ? getLabTestName(selectedLabTest) || `#${labTestId}`
                    : t("projectReports.selectLabTest")}
                </span>
                <span>▼</span>
              </div>
              {labDropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "#14263b",
                    border: "1px solid #333",
                    borderRadius: "6px",
                    marginTop: "4px",
                    maxHeight: "400px",
                    overflowY: "auto",
                    zIndex: 1000,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                  }}
                >
                  <div
                    style={{ padding: "8px", borderBottom: "1px solid #333" }}
                  >
                    <input
                      ref={labSearchInputRef}
                      type="text"
                      placeholder={t("common.search", {
                        defaultValue: isRTL ? "بحث..." : "Search...",
                      })}
                      value={labSearchTerm}
                      onChange={e => setLabSearchTerm(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{
                        width: "100%",
                        padding: "8px",
                        backgroundColor: "#0d1117",
                        border: "none",
                        borderBottom: "1px solid #333",
                        color: "white",
                        borderRadius: "6px 6px 0 0",
                        textAlign: labelAlign,
                        direction: isRTL ? "rtl" : "ltr",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      padding: "8px",
                      borderTop: "1px solid #333",
                      background: "#14263b",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "4px",
                        marginBottom: "6px",
                      }}
                    >
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          setLabPage(1);
                        }}
                        disabled={labPage === 1}
                        style={{
                          padding: "4px 8px",
                          backgroundColor:
                            labPage === 1 ? "#2a2a2a" : "#1a1f2e",
                          color: labPage === 1 ? "#666" : "white",
                          border: "1px solid #444",
                          borderRadius: "4px",
                          cursor: labPage === 1 ? "not-allowed" : "pointer",
                          fontSize: "10px",
                          fontWeight: "bold",
                          minWidth: "28px",
                        }}
                      >
                        ««
                      </button>
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          setLabPage(prev => Math.max(1, prev - 1));
                        }}
                        disabled={labPage === 1}
                        style={{
                          padding: "4px 8px",
                          backgroundColor:
                            labPage === 1 ? "#2a2a2a" : "#1a1f2e",
                          color: labPage === 1 ? "#666" : "white",
                          border: "1px solid #444",
                          borderRadius: "4px",
                          cursor: labPage === 1 ? "not-allowed" : "pointer",
                          fontSize: "10px",
                          fontWeight: "bold",
                          minWidth: "28px",
                        }}
                      >
                        ‹
                      </button>
                      <span
                        style={{
                          color: "#ccc",
                          fontSize: "11px",
                          padding: "0 8px",
                        }}
                      >
                        {t("projectReports.page")} {labPage}
                      </span>
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          setLabPage(prev =>
                            Math.min(prev + 1, labTestMaxPage),
                          );
                        }}
                        disabled={labPage >= labTestMaxPage}
                        style={{
                          padding: "4px 8px",
                          backgroundColor: "#1a1f2e",
                          color: labPage >= labTestMaxPage ? "#666" : "white",
                          border: "1px solid #444",
                          borderRadius: "4px",
                          cursor:
                            labPage >= labTestMaxPage
                              ? "not-allowed"
                              : "pointer",
                          fontSize: "10px",
                          fontWeight: "bold",
                          minWidth: "28px",
                          opacity: labPage >= labTestMaxPage ? 0.5 : 1,
                        }}
                      >
                        ›
                      </button>
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          setLabPage(labTestMaxPage);
                        }}
                        disabled={labPage >= labTestMaxPage}
                        style={{
                          padding: "4px 8px",
                          backgroundColor:
                            labPage >= labTestMaxPage ? "#2a2a2a" : "#1a1f2e",
                          color: labPage >= labTestMaxPage ? "#666" : "white",
                          border: "1px solid #444",
                          borderRadius: "4px",
                          cursor:
                            labPage >= labTestMaxPage
                              ? "not-allowed"
                              : "pointer",
                          fontSize: "10px",
                          fontWeight: "bold",
                          minWidth: "28px",
                        }}
                      >
                        ››
                      </button>
                    </div>
                  </div>
                  <div style={{ maxHeight: "260px", overflowY: "auto" }}>
                    {filteredLabTests.length === 0 ? (
                      <div
                        style={{
                          padding: "20px",
                          textAlign: "center",
                          color: "#ccc",
                        }}
                      >
                        {t("projectReports.noData")}
                      </div>
                    ) : (
                      filteredLabTests.map(lab => (
                        <div
                          key={lab.id}
                          onClick={() => {
                            setLabTestId(String(lab.id));
                            setLabDropdownOpen(false);
                            setLabSearchTerm("");
                          }}
                          style={{
                            padding: "10px 12px",
                            cursor: "pointer",
                            borderTop: "1px solid #333",
                            color: "white",
                            backgroundColor:
                              String(labTestId) === String(lab.id)
                                ? "#2a2a2a"
                                : "transparent",
                            transition: "background-color 0.2s",
                            textAlign: labelAlign,
                            direction: isRTL ? "rtl" : "ltr",
                          }}
                        >
                          {lab.code ? `${lab.code} - ` : ""}
                          {getLabTestName(lab) || `#${lab.id}`}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div>
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: labelAlign,
                }}
              >
                {t("projectReports.fromDate")}
              </label>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                }}
              />
            </div>

            <div>
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: labelAlign,
                }}
              >
                {t("projectReports.toDate")}
              </label>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                }}
              />
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            <button
              type="submit"
              disabled={isLoading}
              style={{
                backgroundColor: isLoading ? "#666" : "#AC2000",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "8px",
                cursor: isLoading ? "not-allowed" : "pointer",
                fontSize: "16px",
                display: "flex",
                alignItems: "center",
                gap: 8,
                opacity: isLoading ? 0.6 : 1,
              }}
            >
              <FaSearch />{" "}
              {isLoading
                ? t("projectReports.loading")
                : t("projectReports.search")}
            </button>
            <button
              type="button"
              onClick={handleResetFilters}
              style={{
                backgroundColor: "#444",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "16px",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {t("projectReports.reset")}
            </button>
            <button
              type="button"
              onClick={handleOpenCreateModal}
              style={{
                backgroundColor: "#28a745",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "16px",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <FaPlus /> {t("projectReports.add")}
            </button>
          </div>
        </form>

        {projectTests && projectTests.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "10px",
              marginBottom: "20px",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={handlePreviewAll}
              style={{
                backgroundColor: "#444",
                color: "white",
                border: "none",
                padding: "12px 20px",
                borderRadius: 8,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                justifyContent: "center",
              }}
            >
              <FaEye /> {t("projectReports.previewAllPDF")}
            </button>
            <button
              onClick={handlePrintAll}
              disabled={isGeneratingPdf}
              style={{
                backgroundColor: isGeneratingPdf ? "#666" : "#17a2b8",
                color: "white",
                border: "none",
                padding: "12px 20px",
                borderRadius: 8,
                cursor: isGeneratingPdf ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                justifyContent: "center",
                opacity: isGeneratingPdf ? 0.6 : 1,
              }}
            >
              <FaPrint />{" "}
              {isGeneratingPdf
                ? t("projectReports.generating")
                : t("projectReports.printAllPDF")}
            </button>
            <button
              onClick={() => handleExportToExcel(projectTests)}
              style={{
                backgroundColor: "#28a745",
                color: "white",
                border: "none",
                padding: "12px 20px",
                borderRadius: 8,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                justifyContent: "center",
              }}
            >
              <FaFileExcel /> {t("projectReports.exportAllExcel")}
            </button>
          </div>
        )}

        <div
          style={{
            backgroundColor: "#1a1f2e",
            padding: "20px",
            borderRadius: "8px",
            marginTop: "20px",
          }}
        >
          {projectTests && projectTests.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  backgroundColor: "#1b3350",
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#AC2000" }}>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        color: "white",
                        border: "1px solid #333",
                      }}
                    >
                      {t("projectReports.project")}
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        color: "white",
                        border: "1px solid #333",
                      }}
                    >
                      {t("projectReports.customer", { defaultValue: "العميل" })}
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        color: "white",
                        border: "1px solid #333",
                      }}
                    >
                      {t("projectReports.labTest")}
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        color: "white",
                        border: "1px solid #333",
                      }}
                    >
                      {t("projectReports.testDate")}
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        color: "white",
                        border: "1px solid #333",
                      }}
                    >
                      {t("projectReports.qty")}
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        color: "white",
                        border: "1px solid #333",
                      }}
                    >
                      {t("projectReports.description")}
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        color: "white",
                        border: "1px solid #333",
                      }}
                    >
                      {t("projectReports.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {projectTests.map((row, idx) => (
                    <tr
                      key={row.id || idx}
                      style={{
                        backgroundColor: idx % 2 === 0 ? "#1b3350" : "#1a1f2e",
                      }}
                    >
                      <td
                        style={{
                          padding: "12px",
                          textAlign: isRTL ? "right" : "left",
                          color: "white",
                          border: "1px solid #333",
                        }}
                      >
                        {isRTL
                          ? row.project?.name_ar ||
                            row.project?.name_en ||
                            row.project?.name
                          : row.project?.name_en ||
                            row.project?.name_ar ||
                            row.project?.name ||
                            "—"}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: isRTL ? "right" : "left",
                          color: "white",
                          border: "1px solid #333",
                        }}
                      >
                        {isRTL
                          ? row.project?.customer?.name_ar ||
                            row.project?.customer?.name_en ||
                            row.project?.customer?.name ||
                            "—"
                          : row.project?.customer?.name_en ||
                            row.project?.customer?.name_ar ||
                            row.project?.customer?.name ||
                            "—"}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: isRTL ? "right" : "left",
                          color: "white",
                          border: "1px solid #333",
                        }}
                      >
                        {isRTL
                          ? row.lab_test?.name_ar || row.lab_test?.name
                          : row.lab_test?.name || row.lab_test?.name_ar || "—"}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: "white",
                          border: "1px solid #333",
                        }}
                      >
                        {formatDate(row.test_date)}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: "white",
                          border: "1px solid #333",
                        }}
                      >
                        {row.qty ?? 0}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: isRTL ? "right" : "left",
                          color: "white",
                          border: "1px solid #333",
                          minWidth: "200px",
                          wordWrap: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {row.description || "—"}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: "white",
                          border: "1px solid #333",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: "8px",
                            flexWrap: "nowrap",
                          }}
                        >
                          <button
                            onClick={() => handlePreview("single", row)}
                            style={{
                              backgroundColor: "#444",
                              color: "white",
                              border: "none",
                              padding: "8px 12px",
                              borderRadius: 6,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <FaEye />
                          </button>
                          <button
                            onClick={() => handlePrintSingle(row)}
                            disabled={isGeneratingPdf}
                            style={{
                              backgroundColor: isGeneratingPdf
                                ? "#666"
                                : "#17a2b8",
                              color: "white",
                              border: "none",
                              padding: "8px 12px",
                              borderRadius: 6,
                              cursor: isGeneratingPdf
                                ? "not-allowed"
                                : "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              opacity: isGeneratingPdf ? 0.6 : 1,
                            }}
                          >
                            <FaFilePdf />
                          </button>
                          <button
                            onClick={() => handleExportToExcel([row])}
                            style={{
                              backgroundColor: "#28a745",
                              color: "white",
                              border: "none",
                              padding: "8px 12px",
                              borderRadius: 6,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <FaFileExcel />
                          </button>
                          <button
                            onClick={() => {
                              setEditingId(row.id);
                              setModalProjectId(
                                String(row.project_id || row.project?.id || ""),
                              );
                              setModalLabTestId(
                                String(
                                  row.lab_test_id || row.lab_test?.id || "",
                                ),
                              );
                              setModalTestDate(row.test_date || "");
                              setModalQty(row.qty ?? 0);
                              setModalDescription(row.description || "");
                              setModalProjectPage(1);
                              setModalLabPage(1);
                              setModalProjectSearchTerm("");
                              setModalLabSearchTerm("");
                              setModalMode("edit");
                            }}
                            style={{
                              backgroundColor: "#6c757d",
                              color: "white",
                              border: "none",
                              padding: "8px 12px",
                              borderRadius: 6,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            ✎
                          </button>
                          <button
                            onClick={() => handleDelete(row.id)}
                            disabled={isDeleting}
                            style={{
                              backgroundColor: "#dc3545",
                              color: "white",
                              border: "none",
                              padding: "8px 12px",
                              borderRadius: 6,
                              cursor: isDeleting ? "not-allowed" : "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              opacity: isDeleting ? 0.6 : 1,
                            }}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div
              style={{ padding: "30px", textAlign: "center", color: "#9ca3af" }}
            >
              {t("projectReports.noData")}
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {meta && meta.total > 0 ? (
          <div
            style={{
              padding: "20px",
              backgroundColor: "#1a1f2e",
              borderRadius: "8px",
              marginTop: "20px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "4px",
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={() =>
                  fetchProjectTests({
                    project_id: projectId,
                    customer_id: reportCustomerId,
                    lab_test_id: labTestId,
                    start_date: startDate,
                    end_date: endDate,
                    page: 1,
                  })
                }
                disabled={meta.current_page === 1}
                style={{
                  padding: "8px 12px",
                  backgroundColor:
                    meta.current_page === 1 ? "#2a2a2a" : "#1a1f2e",
                  color: meta.current_page === 1 ? "#666" : "white",
                  border: "1px solid #444",
                  borderRadius: "4px",
                  cursor: meta.current_page === 1 ? "not-allowed" : "pointer",
                  fontSize: "12px",
                  fontWeight: "bold",
                  minWidth: "32px",
                }}
              >
                ««
              </button>
              <button
                onClick={() =>
                  fetchProjectTests({
                    project_id: projectId,
                    customer_id: reportCustomerId,
                    lab_test_id: labTestId,
                    start_date: startDate,
                    end_date: endDate,
                    page: Math.max(1, meta.current_page - 1),
                  })
                }
                disabled={meta.current_page === 1}
                style={{
                  padding: "8px 12px",
                  backgroundColor:
                    meta.current_page === 1 ? "#2a2a2a" : "#1a1f2e",
                  color: meta.current_page === 1 ? "#666" : "white",
                  border: "1px solid #444",
                  borderRadius: "4px",
                  cursor: meta.current_page === 1 ? "not-allowed" : "pointer",
                  fontSize: "12px",
                  fontWeight: "bold",
                  minWidth: "32px",
                }}
              >
                ‹
              </button>
              {meta.links &&
                meta.links.map((link, idx) => {
                  const pageNum = link.page || null;
                  const isActive = link.active;
                  const isClickable = link.url !== null;

                  if (pageNum === null) return null;

                  return (
                    <button
                      key={idx}
                      onClick={() => {
                        if (isClickable) {
                          fetchProjectTests({
                            project_id: projectId,
                            customer_id: reportCustomerId,
                            lab_test_id: labTestId,
                            start_date: startDate,
                            end_date: endDate,
                            page: pageNum,
                          });
                        }
                      }}
                      disabled={!isClickable}
                      style={{
                        padding: "8px 12px",
                        backgroundColor: isActive ? "#AC2000" : "#1a1f2e",
                        color: isActive ? "white" : "#ccc",
                        border: isActive
                          ? "1px solid #AC2000"
                          : "1px solid #444",
                        borderRadius: "4px",
                        cursor: isClickable ? "pointer" : "not-allowed",
                        fontSize: "12px",
                        fontWeight: isActive ? "bold" : "normal",
                        minWidth: "32px",
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              <button
                onClick={() =>
                  fetchProjectTests({
                    project_id: projectId,
                    customer_id: reportCustomerId,
                    lab_test_id: labTestId,
                    start_date: startDate,
                    end_date: endDate,
                    page: Math.min(meta.last_page, meta.current_page + 1),
                  })
                }
                disabled={meta.current_page === meta.last_page}
                style={{
                  padding: "8px 12px",
                  backgroundColor:
                    meta.current_page === meta.last_page
                      ? "#2a2a2a"
                      : "#1a1f2e",
                  color:
                    meta.current_page === meta.last_page ? "#666" : "white",
                  border: "1px solid #444",
                  borderRadius: "4px",
                  cursor:
                    meta.current_page === meta.last_page
                      ? "not-allowed"
                      : "pointer",
                  fontSize: "12px",
                  fontWeight: "bold",
                  minWidth: "32px",
                }}
              >
                ›
              </button>
              <button
                onClick={() =>
                  fetchProjectTests({
                    project_id: projectId,
                    customer_id: reportCustomerId,
                    lab_test_id: labTestId,
                    start_date: startDate,
                    end_date: endDate,
                    page: meta.last_page,
                  })
                }
                disabled={meta.current_page === meta.last_page}
                style={{
                  padding: "8px 12px",
                  backgroundColor:
                    meta.current_page === meta.last_page
                      ? "#2a2a2a"
                      : "#1a1f2e",
                  color:
                    meta.current_page === meta.last_page ? "#666" : "white",
                  border: "1px solid #444",
                  borderRadius: "4px",
                  cursor:
                    meta.current_page === meta.last_page
                      ? "not-allowed"
                      : "pointer",
                  fontSize: "12px",
                  fontWeight: "bold",
                  minWidth: "32px",
                }}
              >
                »»
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {/* Hidden print template */}
      {pdfType === "single" && selectedTest && (
        <div
          id="project-tests-pdf-single"
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "-9999px",
            top: 0,
            width: "794px",
            height: "1123px",
            backgroundColor: "#ffffff",
            color: "#000000",
            fontFamily: '"Times New Roman Bold", "Times", serif',
            fontWeight: "bold",
            padding: "40px",
            boxSizing: "border-box",
            overflow: "visible",
          }}
        >
          <div
            style={{
              border: "5px solid #000",
              padding: "3px",
              backgroundColor: "#ffffff",
              boxSizing: "border-box",
              width: "100%",
              height: "1043px",
              overflow: "visible",
            }}
          >
            <div
              style={{
                border: "2px solid #000",
                minHeight: "1033px",
                height: "1033px",
                padding: "18px 18px 10px",
                position: "relative",
                display: "flex",
                flexDirection: "column",
                fontFamily: '"Times New Roman Bold", "Times", serif',
                fontWeight: "bold",
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
                textRendering: "optimizeLegibility",
                direction: isRTL ? "rtl" : "ltr",
                backgroundColor: "#ffffff",
                boxSizing: "border-box",
                width: "100%",
                overflow: "visible",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundImage: `url(${headerLogo})`,
                  backgroundSize: "95%",
                  backgroundPosition: "center center",
                  backgroundRepeat: "no-repeat",
                  opacity: 0.08,
                  zIndex: 999,
                  pointerEvents: "none",
                }}
              />
              <div
                style={{
                  position: "relative",
                  zIndex: 1,
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: "100%",
                }}
              >
                <img
                  src={`${publicUrl}/HeaderAce.png`}
                  alt="Header"
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    marginBottom: "8px",
                    borderBottom: "4px double #000",
                    paddingBottom: "8px",
                    flexShrink: 0,
                  }}
                  crossOrigin="anonymous"
                />
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "visible",
                    minHeight: 0,
                    paddingBottom: "120px",
                  }}
                >
                  <div
                    style={{
                      marginTop: "12px",
                      textAlign: "center",
                      marginBottom: "20px",
                    }}
                  >
                    <h2
                      style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color: "#000080",
                        margin: 0,
                      }}
                    >
                      {t("projectReports.projectTests")}
                    </h2>
                    <p style={{ fontSize: "12px", margin: "0 0 4px", color: "#333", fontFamily: '"Times New Roman", serif', fontWeight: "normal" }}>
                      {new Date().toLocaleDateString(isRTL ? "ar-SA" : "en-US", { year: "numeric", month: "long", day: "numeric" })}
                    </p>
                  </div>
                  <div style={{ color: "#000", fontSize: "12px" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        border: "1px solid #000",
                        marginBottom: "15px",
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            backgroundColor: "rgba(238, 242, 247, 0.7)",
                            fontWeight: "bold",
                          }}
                        >
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              width: "36px",
                            }}
                          >
                            #
                          </th>
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                            }}
                          >
                            {t("projectReports.project")}
                          </th>
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                            }}
                          >
                            {t("projectReports.customer", { defaultValue: "العميل" })}
                          </th>
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                            }}
                          >
                            {t("projectReports.labTest")}
                          </th>
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                            }}
                          >
                            {t("projectReports.testDate")}
                          </th>
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                            }}
                          >
                            {t("projectReports.qty")}
                          </th>
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                            }}
                          >
                            {t("projectReports.description")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {[selectedTest].map((row, idx) => (
                          <tr
                            key={row.id || idx}
                            style={{
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            <td
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                                width: "36px",
                              }}
                            >
                              {idx + 1}
                            </td>
                            <td
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: isRTL ? "right" : "left",
                              }}
                            >
                              {isRTL
                                ? row.project?.name_ar ||
                                  row.project?.name_en ||
                                  row.project?.name
                                : row.project?.name_en ||
                                  row.project?.name_ar ||
                                  row.project?.name ||
                                  "—"}
                            </td>
                            <td
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: isRTL ? "right" : "left",
                              }}
                            >
                              {isRTL
                                ? row.project?.customer?.name_ar ||
                                  row.project?.customer?.name_en ||
                                  row.project?.customer?.name ||
                                  "—"
                                : row.project?.customer?.name_en ||
                                  row.project?.customer?.name_ar ||
                                  row.project?.customer?.name ||
                                  "—"}
                            </td>
                            <td
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: isRTL ? "right" : "left",
                              }}
                            >
                              {isRTL
                                ? row.lab_test?.name_ar || row.lab_test?.name
                                : row.lab_test?.name ||
                                  row.lab_test?.name_ar ||
                                  "—"}
                            </td>
                            <td
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                              }}
                            >
                              {formatDate(row.test_date)}
                            </td>
                            <td
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                              }}
                            >
                              {row.qty ?? 0}
                            </td>
                            <td
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                              }}
                            >
                              {row.description || "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div
                  style={{
                    position: "absolute",
                    bottom: "10px",
                    left: "18px",
                    right: "18px",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={`${publicUrl}/FooterAce.png`}
                    alt="Footer"
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                        borderTop: "4px double #000",
                        paddingTop: "8px",
                    }}
                    crossOrigin="anonymous"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hidden print template for all */}
      {pdfType === "all" &&
        pdfChunks &&
        pdfChunks.length > 0 &&
        pdfChunks.map((chunk, chunkIndex) => (
          <div
            key={chunkIndex}
            id={`project-tests-pdf-all-${chunkIndex}`}
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-9999px",
              top: 0,
              width: "794px",
              height: "1123px",
              backgroundColor: "#ffffff",
              color: "#000000",
              fontFamily: '"Times New Roman Bold", "Times", serif',
              fontWeight: "bold",
              padding: "40px",
              boxSizing: "border-box",
              overflow: "visible",
            }}
          >
            <div
              style={{
                border: "5px solid #000",
                padding: "3px",
                backgroundColor: "#ffffff",
                boxSizing: "border-box",
                width: "100%",
                height: "1043px",
                overflow: "visible",
              }}
            >
              <div
                style={{
                  border: "2px solid #000",
                  minHeight: "1033px",
                  height: "1033px",
                  padding: "18px 18px 10px",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  fontFamily: '"Times New Roman Bold", "Times", serif',
                  fontWeight: "bold",
                  WebkitFontSmoothing: "antialiased",
                  MozOsxFontSmoothing: "grayscale",
                  textRendering: "optimizeLegibility",
                  direction: isRTL ? "rtl" : "ltr",
                  backgroundColor: "#ffffff",
                  boxSizing: "border-box",
                  width: "100%",
                  overflow: "visible",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundImage: `url(${headerLogo})`,
                    backgroundSize: "95%",
                    backgroundPosition: "center center",
                    backgroundRepeat: "no-repeat",
                    opacity: 0.08,
                    zIndex: 999,
                    pointerEvents: "none",
                  }}
                />
                <div
                  style={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    minHeight: "100%",
                  }}
                >
                  <img
                    src={`${publicUrl}/HeaderAce.png`}
                    alt="Header"
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                      marginBottom: "8px",
                      borderBottom: "4px double #000",
                      paddingBottom: "8px",
                      flexShrink: 0,
                    }}
                    crossOrigin="anonymous"
                  />
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "visible",
                      minHeight: 0,
                      paddingBottom: "120px",
                    }}
                  >
                    <div
                      style={{
                        marginTop: "12px",
                        textAlign: "center",
                        marginBottom: "20px",
                      }}
                    >
                      <h2
                        style={{
                          fontSize: "20px",
                          fontWeight: "700",
                          color: "#000080",
                          margin: 0,
                        }}
                      >
                        {t("projectReports.projectTests")}
                      </h2>
                      <p style={{ fontSize: "12px", margin: "0 0 4px", color: "#333", fontFamily: '"Times New Roman", serif', fontWeight: "normal" }}>
                        {new Date().toLocaleDateString(isRTL ? "ar-SA" : "en-US", { year: "numeric", month: "long", day: "numeric" })}
                      </p>
                      {(projectId || reportCustomerId || labTestId || startDate || endDate) && (
                        <div style={{ fontSize: "11px", color: "#555", direction: isRTL ? "rtl" : "ltr", fontFamily: '"Times New Roman", serif', fontWeight: "normal" }}>
                          {[
                            selectedProject && `${t("projectReports.project", { defaultValue: "المشروع" })}: ${getProjectName(selectedProject)}`,
                            selectedReportCustomer && `${t("projectReports.customer", { defaultValue: "العميل" })}: ${getCustomerName(selectedReportCustomer)}`,
                            selectedLabTest && `${t("projectReports.labTest", { defaultValue: "اختبار المعمل" })}: ${getLabTestName(selectedLabTest)}`,
                            startDate && `${t("projectReports.fromDate", { defaultValue: "من" })}: ${formatDate(startDate)}`,
                            endDate && `${t("projectReports.toDate", { defaultValue: "إلى" })}: ${formatDate(endDate)}`,
                          ].filter(Boolean).join(" | ")}
                        </div>
                      )}
                    </div>
                    <div style={{ color: "#000", fontSize: "12px" }}>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          border: "1px solid #000",
                          marginBottom: "15px",
                        }}
                      >
                        <thead>
                          <tr
                            style={{
                              backgroundColor: "rgba(238, 242, 247, 0.7)",
                              fontWeight: "bold",
                            }}
                          >
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                                width: "36px",
                              }}
                            >
                              #
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                              }}
                            >
                              {t("projectReports.project")}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                              }}
                            >
                              {t("projectReports.customer", { defaultValue: "العميل" })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                              }}
                            >
                              {t("projectReports.labTest")}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                              }}
                            >
                              {t("projectReports.testDate")}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                              }}
                            >
                              {t("projectReports.qty")}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                              }}
                            >
                              {t("projectReports.description")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {chunk.map((row, idx) => (
                            <tr
                              key={row.id || idx}
                              style={{
                                backgroundColor:
                                  idx % 2 === 0
                                    ? "rgba(255, 255, 255, 0.7)"
                                    : "rgba(255, 255, 255, 0.5)",
                              }}
                            >
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                  width: "36px",
                                }}
                              >
                                {chunkIndex * 20 + idx + 1}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: isRTL ? "right" : "left",
                                }}
                              >
                                {isRTL
                                  ? row.project?.name_ar ||
                                    row.project?.name_en ||
                                    row.project?.name
                                  : row.project?.name_en ||
                                    row.project?.name_ar ||
                                    row.project?.name ||
                                    "—"}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: isRTL ? "right" : "left",
                                }}
                              >
                                {isRTL
                                  ? row.project?.customer?.name_ar ||
                                    row.project?.customer?.name_en ||
                                    row.project?.customer?.name ||
                                    "—"
                                  : row.project?.customer?.name_en ||
                                    row.project?.customer?.name_ar ||
                                    row.project?.customer?.name ||
                                    "—"}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: isRTL ? "right" : "left",
                                }}
                              >
                                {isRTL
                                  ? row.lab_test?.name_ar || row.lab_test?.name
                                  : row.lab_test?.name ||
                                    row.lab_test?.name_ar ||
                                    "—"}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                }}
                              >
                                {formatDate(row.test_date)}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                }}
                              >
                                {row.qty ?? 0}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                }}
                              >
                                {row.description || "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div
                    style={{
                      position: "absolute",
                      bottom: "10px",
                      left: "18px",
                      right: "18px",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={`${publicUrl}/FooterAce.png`}
                      alt="Footer"
                      style={{
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
                          borderTop: "4px double #000",
                          paddingTop: "8px",
                      }}
                      crossOrigin="anonymous"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

      {/* Preview Modal */}
      <PdfPreviewModal
        isOpen={
          isPreviewOpen &&
          ((previewType === "all" && projectTests && projectTests.length > 0) ||
            (previewType === "single" && selectedTest))
        }
        title={t("projectReports.projectTests")}
        onClose={() => setIsPreviewOpen(false)}
        onPrint={previewType === "all" ? handlePrintAll : undefined}
        isGeneratingPdf={isGeneratingPdf}
      >
        <div
          id="preview-project-tests-pdf-all"
          style={{
            width: "794px",
            height: "1123px",
            backgroundColor: "#ffffff",
            color: "#000000",
            fontFamily: '"Times New Roman Bold", "Times", serif',
            fontWeight: "bold",
            padding: "40px",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              border: "5px solid #000",
              padding: "3px",
              backgroundColor: "#ffffff",
              boxSizing: "border-box",
              width: "100%",
              height: "1043px",
              overflow: "visible",
            }}
          >
            <div
              style={{
                border: "2px solid #000",
                minHeight: "1033px",
                height: "1033px",
                padding: "18px 18px 10px",
                position: "relative",
                display: "flex",
                flexDirection: "column",
                fontFamily: '"Times New Roman Bold", "Times", serif',
                fontWeight: "bold",
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
                textRendering: "optimizeLegibility",
                direction: isRTL ? "rtl" : "ltr",
                backgroundColor: "#ffffff",
                boxSizing: "border-box",
                width: "100%",
                overflow: "visible",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundImage: `url(${headerLogo})`,
                  backgroundSize: "95%",
                  backgroundPosition: "center center",
                  backgroundRepeat: "no-repeat",
                  opacity: 0.08,
                  zIndex: 999,
                  pointerEvents: "none",
                }}
              />
              <div
                style={{
                  position: "relative",
                  zIndex: 1,
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: "100%",
                }}
              >
                <img
                  src={`${publicUrl}/HeaderAce.png`}
                  alt="Header"
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    marginBottom: "8px",
                    borderBottom: "4px double #000",
                    paddingBottom: "8px",
                    flexShrink: 0,
                  }}
                  crossOrigin="anonymous"
                />
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "visible",
                    minHeight: 0,
                    paddingBottom: "120px",
                  }}
                >
                  <div
                    style={{
                      marginTop: "12px",
                      textAlign: "center",
                      marginBottom: "20px",
                    }}
                  >
                    <h2
                      style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color: "#000080",
                        margin: 0,
                      }}
                    >
                      {t("projectReports.projectTests")}
                    </h2>
                    <p style={{ fontSize: "12px", margin: "0 0 4px", color: "#333", fontFamily: '"Times New Roman", serif', fontWeight: "normal" }}>
                      {new Date().toLocaleDateString(isRTL ? "ar-SA" : "en-US", { year: "numeric", month: "long", day: "numeric" })}
                    </p>
                    {(projectId || reportCustomerId || labTestId || startDate || endDate) && (
                      <div style={{ fontSize: "11px", color: "#555", direction: isRTL ? "rtl" : "ltr", fontFamily: '"Times New Roman", serif', fontWeight: "normal" }}>
                        {[
                          selectedProject && `${t("projectReports.project", { defaultValue: "المشروع" })}: ${getProjectName(selectedProject)}`,
                          selectedReportCustomer && `${t("projectReports.customer", { defaultValue: "العميل" })}: ${getCustomerName(selectedReportCustomer)}`,
                          selectedLabTest && `${t("projectReports.labTest", { defaultValue: "اختبار المعمل" })}: ${getLabTestName(selectedLabTest)}`,
                          startDate && `${t("projectReports.fromDate", { defaultValue: "من" })}: ${formatDate(startDate)}`,
                          endDate && `${t("projectReports.toDate", { defaultValue: "إلى" })}: ${formatDate(endDate)}`,
                        ].filter(Boolean).join(" | ")}
                      </div>
                    )}
                  </div>
                  <div style={{ color: "#000", fontSize: "12px" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        border: "1px solid #000",
                        marginBottom: "15px",
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            backgroundColor: "rgba(238, 242, 247, 0.7)",
                            fontWeight: "bold",
                          }}
                        >
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              width: "36px",
                            }}
                          >
                            #
                          </th>
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                            }}
                          >
                            {t("projectReports.project")}
                          </th>
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                            }}
                          >
                            {t("projectReports.customer", { defaultValue: "العميل" })}
                          </th>
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                            }}
                          >
                            {t("projectReports.labTest")}
                          </th>
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                            }}
                          >
                            {t("projectReports.testDate")}
                          </th>
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                            }}
                          >
                            {t("projectReports.qty")}
                          </th>
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                            }}
                          >
                            {t("projectReports.description")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(previewType === "single" && selectedTest
                          ? [selectedTest]
                          : projectTests
                        ).map((row, idx) => (
                          <tr
                            key={row.id || idx}
                            style={{
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            <td
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                                width: "36px",
                              }}
                            >
                              {idx + 1}
                            </td>
                            <td
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: isRTL ? "right" : "left",
                              }}
                            >
                              {isRTL
                                ? row.project?.name_ar ||
                                  row.project?.name_en ||
                                  row.project?.name
                                : row.project?.name_en ||
                                  row.project?.name_ar ||
                                  row.project?.name ||
                                  "—"}
                            </td>
                            <td
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: isRTL ? "right" : "left",
                              }}
                            >
                              {isRTL
                                ? row.project?.customer?.name_ar ||
                                  row.project?.customer?.name_en ||
                                  row.project?.customer?.name ||
                                  "—"
                                : row.project?.customer?.name_en ||
                                  row.project?.customer?.name_ar ||
                                  row.project?.customer?.name ||
                                  "—"}
                            </td>
                            <td
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: isRTL ? "right" : "left",
                              }}
                            >
                              {isRTL
                                ? row.lab_test?.name_ar || row.lab_test?.name
                                : row.lab_test?.name ||
                                  row.lab_test?.name_ar ||
                                  "—"}
                            </td>
                            <td
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                              }}
                            >
                              {formatDate(row.test_date)}
                            </td>
                            <td
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                              }}
                            >
                              {row.qty ?? 0}
                            </td>
                            <td
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                              }}
                            >
                              {row.description || "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div
                  style={{
                    position: "absolute",
                    bottom: "10px",
                    left: "18px",
                    right: "18px",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={`${publicUrl}/FooterAce.png`}
                    alt="Footer"
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                        borderTop: "4px double #000",
                        paddingTop: "8px",
                    }}
                    crossOrigin="anonymous"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </PdfPreviewModal>

      {/* Entry Modal (Create or Edit) */}
      {isEntryModalOpen &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              zIndex: 999999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
            }}
            onClick={e => {
              if (e.target === e.currentTarget) {
                setModalMode(null);
                setEditingId(null);
              }
            }}
          >
            <div
              style={{
                backgroundColor: "#1b3350",
                borderRadius: "12px",
                padding: "24px",
                border: "1px solid #333",
                width: "100%",
                maxWidth: "600px",
                maxHeight: "90vh",
                overflowY: "auto",
              }}
              onClick={e => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <h2 style={{ color: "white", margin: 0 }}>
                  {modalMode === "create"
                    ? t("projectReports.add")
                    : t("projectReports.update")}
                </h2>
                <button
                  onClick={() => {
                    setModalMode(null);
                    setEditingId(null);
                  }}
                  style={{
                    backgroundColor: "transparent",
                    border: "none",
                    color: "white",
                    fontSize: "24px",
                    cursor: "pointer",
                    padding: "0",
                    width: "30px",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaTimes />
                </button>
              </div>

              <div style={{ display: "grid", gap: "15px" }}>
                {/* Project dropdown */}
                <div
                  style={{ position: "relative" }}
                  className="modal-project-dropdown-container"
                >
                  <label
                    style={{
                      color: "#ccc",
                      fontSize: "14px",
                      display: "block",
                      marginBottom: "8px",
                      textAlign: labelAlign,
                    }}
                  >
                    {t("projectReports.project")}
                  </label>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "flex-start",
                    }}
                  >
                    <div
                      onClick={() => {
                        setModalProjectDropdownOpen(!modalProjectDropdownOpen);
                        if (!modalProjectDropdownOpen) {
                          setTimeout(() => {
                            if (modalProjectSearchInputRef.current)
                              modalProjectSearchInputRef.current.focus();
                          }, 100);
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: "10px",
                        backgroundColor: "#14263b",
                        border: "1px solid #333",
                        borderRadius: "6px",
                        color: "white",
                        fontSize: "16px",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          flex: 1,
                          textAlign: labelAlign,
                          direction: isRTL ? "rtl" : "ltr",
                        }}
                      >
                        {Array.isArray(projects) &&
                        projects.find(
                          p => String(p.id) === String(modalProjectId),
                        )
                          ? getProjectName(
                              projects.find(
                                p => String(p.id) === String(modalProjectId),
                              ),
                            ) || `#${modalProjectId}`
                          : t("projectReports.selectProject")}
                      </span>
                      <span>▼</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAddProjectModal(true)}
                      style={{
                        backgroundColor: "#AC2000",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        padding: "12px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: "44px",
                        height: "44px",
                      }}
                      title={t("projectReports.addProject", {
                        defaultValue: "إضافة مشروع جديد",
                      })}
                    >
                      <FaPlus />
                    </button>
                  </div>
                  {modalProjectDropdownOpen && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        backgroundColor: "#14263b",
                        border: "1px solid #333",
                        borderRadius: "6px",
                        marginTop: "4px",
                        maxHeight: "400px",
                        overflowY: "auto",
                        zIndex: 1000,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                      }}
                    >
                      <div
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #333",
                        }}
                      >
                        <input
                          ref={modalProjectSearchInputRef}
                          type="text"
                          placeholder={t("common.search", {
                            defaultValue: isRTL ? "بحث..." : "Search...",
                          })}
                          value={modalProjectSearchTerm}
                          onChange={e =>
                            setModalProjectSearchTerm(e.target.value)
                          }
                          onClick={e => e.stopPropagation()}
                          style={{
                            width: "100%",
                            padding: "8px",
                            backgroundColor: "#0d1117",
                            border: "none",
                            borderBottom: "1px solid #333",
                            color: "white",
                            borderRadius: "6px 6px 0 0",
                            textAlign: labelAlign,
                            direction: isRTL ? "rtl" : "ltr",
                          }}
                        />
                      </div>
                      <div
                        style={{
                          padding: "8px",
                          borderTop: "1px solid #333",
                          background: "#14263b",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: "4px",
                            marginBottom: "6px",
                          }}
                        >
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              setModalProjectPage(1);
                            }}
                            disabled={modalProjectPage === 1}
                            style={{
                              padding: "4px 8px",
                              backgroundColor:
                                modalProjectPage === 1 ? "#2a2a2a" : "#1a1f2e",
                              color: modalProjectPage === 1 ? "#666" : "white",
                              border: "1px solid #444",
                              borderRadius: "4px",
                              cursor:
                                modalProjectPage === 1
                                  ? "not-allowed"
                                  : "pointer",
                              fontSize: "10px",
                              fontWeight: "bold",
                              minWidth: "28px",
                            }}
                          >
                            ««
                          </button>
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              setModalProjectPage(prev =>
                                Math.max(1, prev - 1),
                              );
                            }}
                            disabled={modalProjectPage === 1}
                            style={{
                              padding: "4px 8px",
                              backgroundColor:
                                modalProjectPage === 1 ? "#2a2a2a" : "#1a1f2e",
                              color: modalProjectPage === 1 ? "#666" : "white",
                              border: "1px solid #444",
                              borderRadius: "4px",
                              cursor:
                                modalProjectPage === 1
                                  ? "not-allowed"
                                  : "pointer",
                              fontSize: "10px",
                              fontWeight: "bold",
                              minWidth: "28px",
                            }}
                          >
                            ‹
                          </button>
                          <span
                            style={{
                              color: "#ccc",
                              fontSize: "11px",
                              padding: "0 8px",
                            }}
                          >
                            {t("projectReports.page")} {modalProjectPage}
                          </span>
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              setModalProjectPage(prev =>
                                Math.min(prev + 1, modalProjectMaxPage),
                              );
                            }}
                            disabled={modalProjectPage >= modalProjectMaxPage}
                            style={{
                              padding: "4px 8px",
                              backgroundColor: "#1a1f2e",
                              color:
                                modalProjectPage >= modalProjectMaxPage
                                  ? "#666"
                                  : "white",
                              border: "1px solid #444",
                              borderRadius: "4px",
                              cursor:
                                modalProjectPage >= modalProjectMaxPage
                                  ? "not-allowed"
                                  : "pointer",
                              fontSize: "10px",
                              fontWeight: "bold",
                              minWidth: "28px",
                              opacity:
                                modalProjectPage >= modalProjectMaxPage
                                  ? 0.5
                                  : 1,
                            }}
                          >
                            ›
                          </button>
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              setModalProjectPage(modalProjectMaxPage);
                            }}
                            disabled={modalProjectPage >= modalProjectMaxPage}
                            style={{
                              padding: "4px 8px",
                              backgroundColor:
                                modalProjectPage >= modalProjectMaxPage
                                  ? "#2a2a2a"
                                  : "#1a1f2e",
                              color:
                                modalProjectPage >= modalProjectMaxPage
                                  ? "#666"
                                  : "white",
                              border: "1px solid #444",
                              borderRadius: "4px",
                              cursor:
                                modalProjectPage >= modalProjectMaxPage
                                  ? "not-allowed"
                                  : "pointer",
                              fontSize: "10px",
                              fontWeight: "bold",
                              minWidth: "28px",
                            }}
                          >
                            ››
                          </button>
                        </div>
                      </div>
                      {filteredModalProjects.length === 0 ? (
                        <div
                          style={{
                            padding: "20px",
                            textAlign: "center",
                            color: "#ccc",
                          }}
                        >
                          {t("projectReports.noData")}
                        </div>
                      ) : (
                        <>
                          {filteredModalProjects.map(p => (
                            <div
                              key={p.id}
                              onClick={() => {
                                setModalProjectId(String(p.id));
                                setModalProjectDropdownOpen(false);
                                setModalProjectSearchTerm("");
                              }}
                              style={{
                                padding: "10px",
                                cursor: "pointer",
                                borderBottom: "1px solid #333",
                                color: "white",
                                backgroundColor:
                                  String(modalProjectId) === String(p.id)
                                    ? "#2a2a2a"
                                    : "transparent",
                                textAlign: labelAlign,
                                direction: isRTL ? "rtl" : "ltr",
                              }}
                            >
                              {p.code ? `${p.code} - ` : ""}
                              {getProjectName(p) || `#${p.id}`}
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Lab tests dropdown */}
                <div
                  style={{ position: "relative" }}
                  className="modal-lab-dropdown-container"
                >
                  <label
                    style={{
                      color: "#ccc",
                      fontSize: "14px",
                      display: "block",
                      marginBottom: "8px",
                      textAlign: labelAlign,
                    }}
                  >
                    {t("projectReports.labTest")}
                  </label>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "flex-start",
                    }}
                  >
                    <div
                      onClick={() => {
                        setModalLabDropdownOpen(!modalLabDropdownOpen);
                        if (!modalLabDropdownOpen) {
                          setTimeout(() => {
                            if (modalLabSearchInputRef.current)
                              modalLabSearchInputRef.current.focus();
                          }, 100);
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: "10px",
                        backgroundColor: "#14263b",
                        border: "1px solid #333",
                        borderRadius: "6px",
                        color: "white",
                        fontSize: "16px",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          flex: 1,
                          textAlign: labelAlign,
                          direction: isRTL ? "rtl" : "ltr",
                        }}
                      >
                        {Array.isArray(labTests) &&
                        labTests.find(
                          tst => String(tst.id) === String(modalLabTestId),
                        )
                          ? getLabTestName(
                              labTests.find(
                                tst =>
                                  String(tst.id) === String(modalLabTestId),
                              ),
                            ) || `#${modalLabTestId}`
                          : t("projectReports.selectLabTest")}
                      </span>
                      <span>▼</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAddTestModal(true)}
                      style={{
                        backgroundColor: "#AC2000",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        padding: "12px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: "44px",
                        height: "44px",
                      }}
                      title={t("projectReports.addTest", {
                        defaultValue: "إضافة اختبار جديد",
                      })}
                    >
                      <FaPlus />
                    </button>
                  </div>
                  {modalLabDropdownOpen && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        backgroundColor: "#14263b",
                        border: "1px solid #333",
                        borderRadius: "6px",
                        marginTop: "4px",
                        maxHeight: "400px",
                        overflowY: "auto",
                        zIndex: 1000,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                      }}
                    >
                      <div
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #333",
                        }}
                      >
                        <input
                          ref={modalLabSearchInputRef}
                          type="text"
                          placeholder={t("common.search", {
                            defaultValue: isRTL ? "بحث..." : "Search...",
                          })}
                          value={modalLabSearchTerm}
                          onChange={e => setModalLabSearchTerm(e.target.value)}
                          onClick={e => e.stopPropagation()}
                          style={{
                            width: "100%",
                            padding: "8px",
                            backgroundColor: "#0d1117",
                            border: "none",
                            borderBottom: "1px solid #333",
                            color: "white",
                            borderRadius: "6px 6px 0 0",
                            textAlign: labelAlign,
                            direction: isRTL ? "rtl" : "ltr",
                          }}
                        />
                      </div>
                      <div
                        style={{
                          padding: "8px",
                          borderTop: "1px solid #333",
                          background: "#14263b",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: "4px",
                            marginBottom: "6px",
                          }}
                        >
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              setModalLabPage(1);
                            }}
                            disabled={modalLabPage === 1}
                            style={{
                              padding: "4px 8px",
                              backgroundColor:
                                modalLabPage === 1 ? "#2a2a2a" : "#1a1f2e",
                              color: modalLabPage === 1 ? "#666" : "white",
                              border: "1px solid #444",
                              borderRadius: "4px",
                              cursor:
                                modalLabPage === 1 ? "not-allowed" : "pointer",
                              fontSize: "10px",
                              fontWeight: "bold",
                              minWidth: "28px",
                            }}
                          >
                            ««
                          </button>
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              setModalLabPage(prev => Math.max(1, prev - 1));
                            }}
                            disabled={modalLabPage === 1}
                            style={{
                              padding: "4px 8px",
                              backgroundColor:
                                modalLabPage === 1 ? "#2a2a2a" : "#1a1f2e",
                              color: modalLabPage === 1 ? "#666" : "white",
                              border: "1px solid #444",
                              borderRadius: "4px",
                              cursor:
                                modalLabPage === 1 ? "not-allowed" : "pointer",
                              fontSize: "10px",
                              fontWeight: "bold",
                              minWidth: "28px",
                            }}
                          >
                            ‹
                          </button>
                          <span
                            style={{
                              color: "#ccc",
                              fontSize: "11px",
                              padding: "0 8px",
                            }}
                          >
                            {t("projectReports.page")} {modalLabPage}
                          </span>
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              setModalLabPage(prev =>
                                Math.min(prev + 1, modalLabTestMaxPage),
                              );
                            }}
                            disabled={modalLabPage >= modalLabTestMaxPage}
                            style={{
                              padding: "4px 8px",
                              backgroundColor: "#1a1f2e",
                              color:
                                modalLabPage >= modalLabTestMaxPage
                                  ? "#666"
                                  : "white",
                              border: "1px solid #444",
                              borderRadius: "4px",
                              cursor:
                                modalLabPage >= modalLabTestMaxPage
                                  ? "not-allowed"
                                  : "pointer",
                              fontSize: "10px",
                              fontWeight: "bold",
                              minWidth: "28px",
                              opacity:
                                modalLabPage >= modalLabTestMaxPage ? 0.5 : 1,
                            }}
                          >
                            ›
                          </button>
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              setModalLabPage(modalLabTestMaxPage);
                            }}
                            disabled={modalLabPage >= modalLabTestMaxPage}
                            style={{
                              padding: "4px 8px",
                              backgroundColor:
                                modalLabPage >= modalLabTestMaxPage
                                  ? "#2a2a2a"
                                  : "#1a1f2e",
                              color:
                                modalLabPage >= modalLabTestMaxPage
                                  ? "#666"
                                  : "white",
                              border: "1px solid #444",
                              borderRadius: "4px",
                              cursor:
                                modalLabPage >= modalLabTestMaxPage
                                  ? "not-allowed"
                                  : "pointer",
                              fontSize: "10px",
                              fontWeight: "bold",
                              minWidth: "28px",
                            }}
                          >
                            ››
                          </button>
                        </div>
                      </div>
                      {filteredModalLabTests.length === 0 ? (
                        <div
                          style={{
                            padding: "20px",
                            textAlign: "center",
                            color: "#ccc",
                          }}
                        >
                          {t("projectReports.noData")}
                        </div>
                      ) : (
                        <>
                          {filteredModalLabTests.map(lab => (
                            <div
                              key={lab.id}
                              onClick={() => {
                                setModalLabTestId(String(lab.id));
                                setModalLabDropdownOpen(false);
                                setModalLabSearchTerm("");
                              }}
                              style={{
                                padding: "10px",
                                cursor: "pointer",
                                borderBottom: "1px solid #333",
                                color: "white",
                                backgroundColor:
                                  String(modalLabTestId) === String(lab.id)
                                    ? "#2a2a2a"
                                    : "transparent",
                                textAlign: labelAlign,
                                direction: isRTL ? "rtl" : "ltr",
                              }}
                            >
                              {lab.code ? `${lab.code} - ` : ""}
                              {getLabTestName(lab) || `#${lab.id}`}
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label
                    style={{
                      color: "#ccc",
                      fontSize: "14px",
                      display: "block",
                      marginBottom: "8px",
                      textAlign: labelAlign,
                    }}
                  >
                    {t("projectReports.testDate")}
                  </label>
                  <input
                    type="date"
                    value={modalTestDate}
                    onChange={e => setModalTestDate(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px",
                      backgroundColor: "#14263b",
                      border: "1px solid #333",
                      borderRadius: "6px",
                      color: "white",
                      fontSize: "16px",
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      color: "#ccc",
                      fontSize: "14px",
                      display: "block",
                      marginBottom: "8px",
                      textAlign: labelAlign,
                    }}
                  >
                    {t("projectReports.qty")}
                  </label>
                  <input
                    type="number"
                    value={modalQty}
                    onChange={e => setModalQty(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px",
                      backgroundColor: "#14263b",
                      border: "1px solid #333",
                      borderRadius: "6px",
                      color: "white",
                      fontSize: "16px",
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      color: "#ccc",
                      fontSize: "14px",
                      display: "block",
                      marginBottom: "8px",
                      textAlign: labelAlign,
                    }}
                  >
                    {t("projectReports.description")}
                  </label>
                  <input
                    type="text"
                    value={modalDescription}
                    onChange={e => setModalDescription(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px",
                      backgroundColor: "#14263b",
                      border: "1px solid #333",
                      borderRadius: "6px",
                      color: "white",
                      fontSize: "16px",
                    }}
                  />
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "10px",
                  marginTop: "20px",
                }}
              >
                <button
                  onClick={
                    modalMode === "create" ? handleCreateSave : handleEditSave
                  }
                  disabled={isSaving}
                  style={{
                    backgroundColor: isSaving ? "#666" : "#28a745",
                    color: "white",
                    border: "none",
                    padding: "12px 24px",
                    borderRadius: "8px",
                    cursor: isSaving ? "not-allowed" : "pointer",
                    fontSize: "16px",
                    opacity: isSaving ? 0.6 : 1,
                  }}
                >
                  {isSaving
                    ? t("projectReports.saving")
                    : modalMode === "create"
                      ? t("projectReports.add")
                      : t("projectReports.update")}
                </button>
                <button
                  onClick={() => {
                    setModalMode(null);
                    setEditingId(null);
                  }}
                  style={{
                    backgroundColor: "#6c757d",
                    color: "white",
                    border: "none",
                    padding: "12px 24px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "16px",
                  }}
                >
                  {t("projectReports.cancel")}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Add Project Modal */}
      {showAddProjectModal &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              zIndex: 999999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
            }}
            onClick={() => setShowAddProjectModal(false)}
          >
            <div
              style={{
                backgroundColor: "#1b3350",
                borderRadius: "12px",
                padding: "30px",
                maxWidth: "900px",
                width: "100%",
                maxHeight: "90vh",
                overflowY: "auto",
                border: "1px solid #333",
                direction: isRTL ? "rtl" : "ltr",
              }}
              onClick={e => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                  flexDirection: isRTL ? "row-reverse" : "row",
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontSize: "20px",
                    color: "white",
                    textAlign: isRTL ? "right" : "left",
                  }}
                >
                  {t("quotations.add.modals.addProject.title", {
                    defaultValue: "إضافة مشروع جديد",
                  })}
                </h2>
                <button
                  onClick={() => {
                    setShowAddProjectModal(false);
                    setProjectForm({
                      code: "",
                      name_ar: "",
                      name_en: "",
                      customer_id: null,
                      start_date: "",
                      end_date: "",
                      status: "planned",
                      contract_value: "",
                      tax_id: null,
                      cost_center_id: null,
                      notes: "",
                    });
                  }}
                  style={{
                    backgroundColor: "transparent",
                    border: "none",
                    color: "white",
                    fontSize: "24px",
                    cursor: "pointer",
                    padding: "0",
                    width: "30px",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaTimes />
                </button>
              </div>
              <form onSubmit={handleAddProject}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                    gap: 16,
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                        fontSize: 14,
                        color: "white",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addProject.code", {
                        defaultValue: "الكود",
                      })}
                    </label>
                    <input
                      type="text"
                      name="code"
                      value={projectForm.code}
                      onChange={handleProjectFormChange}
                      onFocus={e => e.stopPropagation()}
                      onBlur={e => e.stopPropagation()}
                      style={{
                        width: "100%",
                        padding: 12,
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: 8,
                        color: "white",
                        fontSize: 14,
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                        fontSize: 14,
                        color: "white",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addProject.nameAr", {
                        defaultValue: "الاسم بالعربية",
                      })}{" "}
                      <span style={{ color: "#dc3545" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name_ar"
                      value={projectForm.name_ar}
                      onChange={handleProjectFormChange}
                      onFocus={e => e.stopPropagation()}
                      onBlur={e => e.stopPropagation()}
                      required
                      style={{
                        width: "100%",
                        padding: 12,
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: 8,
                        color: "white",
                        fontSize: 14,
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                        fontSize: 14,
                        color: "white",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addProject.nameEn", {
                        defaultValue: "الاسم بالإنجليزية",
                      })}
                    </label>
                    <input
                      type="text"
                      name="name_en"
                      value={projectForm.name_en}
                      onChange={handleProjectFormChange}
                      onFocus={e => e.stopPropagation()}
                      onBlur={e => e.stopPropagation()}
                      style={{
                        width: "100%",
                        padding: 12,
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: 8,
                        color: "white",
                        fontSize: 14,
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    />
                  </div>
                  <div style={{ position: "relative" }} data-dropdown-container>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                        fontSize: 14,
                        color: "white",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addProject.customer", {
                        defaultValue: "العميل",
                      })}
                    </label>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "flex-start",
                      }}
                    >
                      <div
                        data-dropdown-toggle
                        onClick={() => {
                          setCustomerDropdownOpen(p => !p);
                          if (customerDropdownOpen) setCustomerSearchTerm("");
                        }}
                        style={{
                          flex: 1,
                          padding: 12,
                          backgroundColor: "#1a1f2e",
                          border: "1px solid #333",
                          borderRadius: 8,
                          color: "white",
                          fontSize: 14,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          cursor: "pointer",
                          boxSizing: "border-box",
                        }}
                      >
                        <span
                          style={{
                            direction: isRTL ? "rtl" : "ltr",
                            textAlign: isRTL ? "right" : "left",
                          }}
                        >
                          {projectForm.customer_id
                            ? (customers || []).find(
                                c =>
                                  String(c.id) ===
                                  String(projectForm.customer_id),
                              )
                              ? renderDropdownItem(
                                  (customers || []).find(
                                    c =>
                                      String(c.id) ===
                                      String(projectForm.customer_id),
                                  ),
                                  "code",
                                  "name_ar",
                                  "name",
                                )
                              : t(
                                  "quotations.add.modals.addProject.selectCustomer",
                                  { defaultValue: "اختر العميل" },
                                )
                            : t(
                                "quotations.add.modals.addProject.selectCustomer",
                                { defaultValue: "اختر العميل" },
                              )}
                        </span>
                        <span style={{ fontSize: 12 }}>▼</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowAddCustomerModal(true)}
                        style={{
                          backgroundColor: "#AC2000",
                          color: "white",
                          border: "none",
                          borderRadius: "8px",
                          padding: "12px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: "44px",
                          height: "44px",
                        }}
                        title={t(
                          "quotations.add.modals.addProject.addCustomer",
                          { defaultValue: "إضافة عميل جديد" },
                        )}
                      >
                        <FaPlus />
                      </button>
                    </div>
                    <DropdownWithSearch
                      isOpen={customerDropdownOpen}
                      onToggle={setCustomerDropdownOpen}
                      searchTerm={customerSearchTerm}
                      onSearchChange={setCustomerSearchTerm}
                      searchInputRef={customerSearchInputRef}
                      items={customers || []}
                      pagination={customersPagination}
                      currentPage={customerCurrentPage}
                      onPageChange={setCustomerCurrentPage}
                      onSelect={id => {
                        setProjectForm(prev => ({ ...prev, customer_id: id }));
                        setCustomerDropdownOpen(false);
                        setCustomerSearchTerm("");
                        setCustomerCurrentPage(1);
                      }}
                      displayValue={() =>
                        projectForm.customer_id
                          ? (customers || []).find(
                              c =>
                                String(c.id) ===
                                String(projectForm.customer_id),
                            )
                            ? renderDropdownItem(
                                (customers || []).find(
                                  c =>
                                    String(c.id) ===
                                    String(projectForm.customer_id),
                                ),
                                "code",
                                "name_ar",
                                "name",
                              )
                            : t(
                                "quotations.add.modals.addProject.selectCustomer",
                                { defaultValue: "اختر العميل" },
                              )
                          : t(
                              "quotations.add.modals.addProject.selectCustomer",
                              { defaultValue: "اختر العميل" },
                            )
                      }
                      placeholder={t(
                        "quotations.add.modals.addProject.selectCustomer",
                        { defaultValue: "اختر العميل" },
                      )}
                      label={t("quotations.add.modals.addProject.customer", {
                        defaultValue: "عميل",
                      })}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                        fontSize: 14,
                        color: "white",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addProject.startDate", {
                        defaultValue: "تاريخ البداية",
                      })}
                    </label>
                    <input
                      type="date"
                      name="start_date"
                      value={projectForm.start_date}
                      onChange={handleProjectFormChange}
                      onFocus={e => e.stopPropagation()}
                      onBlur={e => e.stopPropagation()}
                      style={{
                        width: "100%",
                        padding: 12,
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: 8,
                        color: "white",
                        fontSize: 14,
                        direction: isRTL ? "rtl" : "ltr",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                        fontSize: 14,
                        color: "white",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addProject.endDate", {
                        defaultValue: "تاريخ النهاية",
                      })}
                    </label>
                    <input
                      type="date"
                      name="end_date"
                      value={projectForm.end_date}
                      onChange={handleProjectFormChange}
                      onFocus={e => e.stopPropagation()}
                      onBlur={e => e.stopPropagation()}
                      style={{
                        width: "100%",
                        padding: 12,
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: 8,
                        color: "white",
                        fontSize: 14,
                        direction: isRTL ? "rtl" : "ltr",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                        fontSize: 14,
                        color: "white",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addProject.status", {
                        defaultValue: "الحالة",
                      })}
                    </label>
                    <select
                      name="status"
                      value={projectForm.status}
                      onChange={handleProjectFormChange}
                      onFocus={e => e.stopPropagation()}
                      onBlur={e => e.stopPropagation()}
                      style={{
                        width: "100%",
                        padding: 12,
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: 8,
                        color: "white",
                        fontSize: 14,
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      <option value="planned">
                        {t(
                          "quotations.add.modals.addProject.statusOptions.planned",
                          { defaultValue: "مخطط" },
                        )}
                      </option>
                      <option value="in_progress">
                        {t(
                          "quotations.add.modals.addProject.statusOptions.inProgress",
                          { defaultValue: "قيد التنفيذ" },
                        )}
                      </option>
                      <option value="paused">
                        {t(
                          "quotations.add.modals.addProject.statusOptions.paused",
                          { defaultValue: "متوقف" },
                        )}
                      </option>
                      <option value="completed">
                        {t(
                          "quotations.add.modals.addProject.statusOptions.completed",
                          { defaultValue: "مكتمل" },
                        )}
                      </option>
                      <option value="cancelled">
                        {t(
                          "quotations.add.modals.addProject.statusOptions.cancelled",
                          { defaultValue: "ملغى" },
                        )}
                      </option>
                    </select>
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                        fontSize: 14,
                        color: "white",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addProject.contractValue", {
                        defaultValue: "قيمة العقد",
                      })}
                    </label>
                    <input
                      type="number"
                      name="contract_value"
                      value={projectForm.contract_value}
                      onChange={handleProjectFormChange}
                      onFocus={e => e.stopPropagation()}
                      onBlur={e => e.stopPropagation()}
                      style={{
                        width: "100%",
                        padding: 12,
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: 8,
                        color: "white",
                        fontSize: 14,
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    />
                  </div>
                  <div style={{ position: "relative" }} data-dropdown-container>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                        fontSize: 14,
                        color: "white",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addProject.tax", {
                        defaultValue: "الضريبة",
                      })}
                    </label>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "flex-start",
                        flexDirection: isRTL ? "row-reverse" : "row",
                      }}
                    >
                      <div
                        data-dropdown-toggle
                        onClick={() => {
                          setTaxDropdownOpen(p => !p);
                          if (taxDropdownOpen) setTaxSearchTerm("");
                        }}
                        style={{
                          flex: 1,
                          padding: 12,
                          backgroundColor: "#1a1f2e",
                          border: "1px solid #333",
                          borderRadius: 8,
                          color: "white",
                          fontSize: 14,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          cursor: "pointer",
                          boxSizing: "border-box",
                          direction: isRTL ? "rtl" : "ltr",
                        }}
                      >
                        <span
                          style={{
                            direction: isRTL ? "rtl" : "ltr",
                            textAlign: isRTL ? "right" : "left",
                          }}
                        >
                          {projectForm.tax_id
                            ? (taxes || []).find(
                                t =>
                                  String(t.id) === String(projectForm.tax_id),
                              )
                              ? isRTL
                                ? (taxes || []).find(
                                    t =>
                                      String(t.id) ===
                                      String(projectForm.tax_id),
                                  ).name_ar ||
                                  (taxes || []).find(
                                    t =>
                                      String(t.id) ===
                                      String(projectForm.tax_id),
                                  ).name_en ||
                                  ""
                                : (taxes || []).find(
                                    t =>
                                      String(t.id) ===
                                      String(projectForm.tax_id),
                                  ).name_en ||
                                  (taxes || []).find(
                                    t =>
                                      String(t.id) ===
                                      String(projectForm.tax_id),
                                  ).name_ar ||
                                  ""
                              : t(
                                  "quotations.add.modals.addProject.selectTax",
                                  { defaultValue: "اختر الضريبة" },
                                )
                            : t("quotations.add.modals.addProject.selectTax", {
                                defaultValue: "اختر الضريبة",
                              })}
                        </span>
                        <span style={{ fontSize: 12 }}>▼</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowAddTaxModal(true)}
                        style={{
                          backgroundColor: "#AC2000",
                          color: "white",
                          border: "none",
                          borderRadius: "8px",
                          padding: "12px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: "44px",
                          height: "44px",
                        }}
                        title={t("quotations.add.modals.addTax.title", {
                          defaultValue: "إضافة ضريبة جديدة",
                        })}
                      >
                        <FaPlus />
                      </button>
                    </div>
                    <DropdownWithSearch
                      isOpen={taxDropdownOpen}
                      onToggle={setTaxDropdownOpen}
                      searchTerm={taxSearchTerm}
                      onSearchChange={setTaxSearchTerm}
                      searchInputRef={taxSearchInputRef}
                      items={taxes || []}
                      pagination={taxesPagination}
                      currentPage={taxCurrentPage}
                      onPageChange={setTaxCurrentPage}
                      onSelect={id => {
                        setProjectForm(prev => ({ ...prev, tax_id: id }));
                        setTaxDropdownOpen(false);
                        setTaxSearchTerm("");
                        setTaxCurrentPage(1);
                      }}
                      displayValue={() =>
                        projectForm.tax_id
                          ? (taxes || []).find(
                              t => String(t.id) === String(projectForm.tax_id),
                            )
                            ? isRTL
                              ? (taxes || []).find(
                                  t =>
                                    String(t.id) === String(projectForm.tax_id),
                                ).name_ar ||
                                (taxes || []).find(
                                  t =>
                                    String(t.id) === String(projectForm.tax_id),
                                ).name_en ||
                                ""
                              : (taxes || []).find(
                                  t =>
                                    String(t.id) === String(projectForm.tax_id),
                                ).name_en ||
                                (taxes || []).find(
                                  t =>
                                    String(t.id) === String(projectForm.tax_id),
                                ).name_ar ||
                                ""
                            : t("quotations.add.modals.addProject.selectTax", {
                                defaultValue: "اختر الضريبة",
                              })
                          : t("quotations.add.modals.addProject.selectTax", {
                              defaultValue: "اختر الضريبة",
                            })
                      }
                      placeholder={t(
                        "quotations.add.modals.addProject.selectTax",
                        { defaultValue: "اختر الضريبة" },
                      )}
                      label={t("quotations.add.modals.addProject.tax", {
                        defaultValue: "ضريبة",
                      })}
                    />
                  </div>
                  <div style={{ position: "relative" }} data-dropdown-container>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                        fontSize: 14,
                        color: "white",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addProject.costCenter", {
                        defaultValue: "مركز التكلفة",
                      })}
                    </label>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "flex-start",
                        flexDirection: isRTL ? "row-reverse" : "row",
                      }}
                    >
                      <div
                        data-dropdown-toggle
                        onClick={() => {
                          setCostCenterDropdownOpen(p => !p);
                          if (costCenterDropdownOpen)
                            setCostCenterSearchTerm("");
                        }}
                        style={{
                          flex: 1,
                          padding: 12,
                          backgroundColor: "#1a1f2e",
                          border: "1px solid #333",
                          borderRadius: 8,
                          color: "white",
                          fontSize: 14,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          cursor: "pointer",
                          boxSizing: "border-box",
                          direction: isRTL ? "rtl" : "ltr",
                        }}
                      >
                        <span
                          style={{
                            direction: isRTL ? "rtl" : "ltr",
                            textAlign: isRTL ? "right" : "left",
                          }}
                        >
                          {projectForm.cost_center_id
                            ? (costCenters || []).find(
                                cc =>
                                  String(cc.id) ===
                                  String(projectForm.cost_center_id),
                              )
                              ? renderDropdownItem(
                                  (costCenters || []).find(
                                    cc =>
                                      String(cc.id) ===
                                      String(projectForm.cost_center_id),
                                  ),
                                  "code",
                                  "name_ar",
                                  "name",
                                )
                              : t(
                                  "quotations.add.modals.addProject.selectCostCenter",
                                  { defaultValue: "اختر مركز التكلفة" },
                                )
                            : t(
                                "quotations.add.modals.addProject.selectCostCenter",
                                { defaultValue: "اختر مركز التكلفة" },
                              )}
                        </span>
                        <span style={{ fontSize: 12 }}>▼</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowAddCostCenterModal(true)}
                        style={{
                          backgroundColor: "#AC2000",
                          color: "white",
                          border: "none",
                          borderRadius: "8px",
                          padding: "12px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: "44px",
                          height: "44px",
                        }}
                        title={t(
                          "quotations.add.modals.addProject.addCostCenter",
                          { defaultValue: "إضافة مركز تكلفة جديد" },
                        )}
                      >
                        <FaPlus />
                      </button>
                    </div>
                    <DropdownWithSearch
                      isOpen={costCenterDropdownOpen}
                      onToggle={setCostCenterDropdownOpen}
                      searchTerm={costCenterSearchTerm}
                      onSearchChange={setCostCenterSearchTerm}
                      searchInputRef={costCenterSearchInputRef}
                      items={costCenters || []}
                      pagination={costCentersPagination}
                      currentPage={costCenterCurrentPage}
                      onPageChange={setCostCenterCurrentPage}
                      onSelect={id => {
                        setProjectForm(prev => ({
                          ...prev,
                          cost_center_id: id,
                        }));
                        setCostCenterDropdownOpen(false);
                        setCostCenterSearchTerm("");
                        setCostCenterCurrentPage(1);
                      }}
                      displayValue={() =>
                        projectForm.cost_center_id
                          ? (costCenters || []).find(
                              cc =>
                                String(cc.id) ===
                                String(projectForm.cost_center_id),
                            )
                            ? renderDropdownItem(
                                (costCenters || []).find(
                                  cc =>
                                    String(cc.id) ===
                                    String(projectForm.cost_center_id),
                                ),
                                "code",
                                "name_ar",
                                "name",
                              )
                            : t(
                                "quotations.add.modals.addProject.selectCostCenter",
                                { defaultValue: "اختر مركز التكلفة" },
                              )
                          : t(
                              "quotations.add.modals.addProject.selectCostCenter",
                              { defaultValue: "اختر مركز التكلفة" },
                            )
                      }
                      placeholder={t(
                        "quotations.add.modals.addProject.selectCostCenter",
                        { defaultValue: "اختر مركز التكلفة" },
                      )}
                      label={t("quotations.add.modals.addProject.costCenter", {
                        defaultValue: "مركز تكلفة",
                      })}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                        fontSize: 14,
                        color: "white",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addProject.notes", {
                        defaultValue: "ملاحظات",
                      })}
                    </label>
                    <textarea
                      name="notes"
                      value={projectForm.notes}
                      onChange={handleProjectFormChange}
                      onFocus={e => e.stopPropagation()}
                      onBlur={e => e.stopPropagation()}
                      onClick={e => e.stopPropagation()}
                      rows={3}
                      style={{
                        width: "100%",
                        padding: 12,
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: 8,
                        color: "white",
                        fontSize: 14,
                        resize: "vertical",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 12,
                    marginTop: "20px",
                    flexDirection: isRTL ? "row-reverse" : "row",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddProjectModal(false);
                      setProjectForm({
                        code: "",
                        name_ar: "",
                        name_en: "",
                        customer_id: null,
                        start_date: "",
                        end_date: "",
                        status: "planned",
                        contract_value: "",
                        tax_id: null,
                        cost_center_id: null,
                        notes: "",
                      });
                    }}
                    style={{
                      backgroundColor: "#666",
                      color: "white",
                      border: "none",
                      borderRadius: 8,
                      padding: "10px 16px",
                      cursor: "pointer",
                    }}
                  >
                    {t("quotations.add.modals.addProject.cancel", {
                      defaultValue: "إلغاء",
                    })}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingProject}
                    style={{
                      backgroundColor: isSubmittingProject ? "#666" : "#AC2000",
                      color: "white",
                      border: "none",
                      borderRadius: 8,
                      padding: "10px 16px",
                      cursor: isSubmittingProject ? "not-allowed" : "pointer",
                      opacity: isSubmittingProject ? 0.7 : 1,
                    }}
                  >
                    {isSubmittingProject
                      ? t("quotations.add.modals.addProject.saving", {
                          defaultValue: "جاري الحفظ...",
                        })
                      : t("quotations.add.modals.addProject.save", {
                          defaultValue: "حفظ",
                        })}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}

      {/* Add Test Modal */}
      {showAddTestModal &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              zIndex: 999999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
            }}
            onClick={() => setShowAddTestModal(false)}
          >
            <div
              style={{
                backgroundColor: "#1b3350",
                borderRadius: "12px",
                padding: "24px",
                border: "1px solid #333",
                width: "100%",
                maxWidth: "600px",
                maxHeight: "90vh",
                overflowY: "auto",
              }}
              onClick={e => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <h2 style={{ color: "white", margin: 0 }}>
                  {t("tasks.add.addTest", {
                    defaultValue: "إضافة اختبار جديد",
                  })}
                </h2>
                <button
                  onClick={() => {
                    setShowAddTestModal(false);
                    setTestForm({
                      name_ar: "",
                      name_en: "",
                      is_active: true,
                    });
                  }}
                  style={{
                    backgroundColor: "transparent",
                    border: "none",
                    color: "white",
                    fontSize: "24px",
                    cursor: "pointer",
                    padding: "0",
                    width: "30px",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaTimes />
                </button>
              </div>
              <form onSubmit={handleAddTest}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                    gap: 16,
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                        fontSize: 14,
                        color: "white",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("tasks.add.testNameAr", {
                        defaultValue: "الاسم بالعربية",
                      })}{" "}
                      <span style={{ color: "#dc3545" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name_ar"
                      value={testForm.name_ar}
                      onChange={e =>
                        setTestForm(prev => ({
                          ...prev,
                          name_ar: e.target.value,
                        }))
                      }
                      required
                      style={{
                        width: "100%",
                        padding: 12,
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: 8,
                        color: "white",
                        fontSize: 14,
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                        fontSize: 14,
                        color: "white",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("tasks.add.testNameEn", {
                        defaultValue: "الاسم بالإنجليزية",
                      })}{" "}
                      <span style={{ color: "#dc3545" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name_en"
                      value={testForm.name_en}
                      onChange={e =>
                        setTestForm(prev => ({
                          ...prev,
                          name_en: e.target.value,
                        }))
                      }
                      required
                      style={{
                        width: "100%",
                        padding: 12,
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: 8,
                        color: "white",
                        fontSize: 14,
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                        fontSize: 14,
                        color: "white",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("tasks.add.status", { defaultValue: "الحالة" })}{" "}
                      <span style={{ color: "#dc3545" }}></span>
                    </label>
                    <select
                      name="is_active"
                      value={testForm.is_active ? "true" : "false"}
                      onChange={e =>
                        setTestForm(prev => ({
                          ...prev,
                          is_active: e.target.value === "true",
                        }))
                      }
                      style={{
                        width: "100%",
                        padding: 12,
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: 8,
                        color: "white",
                        fontSize: 14,
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      <option value="true">
                        {t("tasks.add.active", { defaultValue: "نشط" })}
                      </option>
                      <option value="false">
                        {t("tasks.add.inactive", { defaultValue: "غير نشط" })}
                      </option>
                    </select>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 12,
                    marginTop: "20px",
                    flexDirection: isRTL ? "row-reverse" : "row",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddTestModal(false);
                      setTestForm({
                        name_ar: "",
                        name_en: "",
                        is_active: true,
                      });
                    }}
                    style={{
                      backgroundColor: "#666",
                      color: "white",
                      border: "none",
                      borderRadius: 8,
                      padding: "10px 16px",
                      cursor: "pointer",
                    }}
                  >
                    {t("tasks.add.cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingTest}
                    style={{
                      backgroundColor: isSubmittingTest ? "#666" : "#28a745",
                      color: "white",
                      border: "none",
                      borderRadius: 8,
                      padding: "10px 16px",
                      cursor: isSubmittingTest ? "not-allowed" : "pointer",
                      opacity: isSubmittingTest ? 0.7 : 1,
                    }}
                  >
                    {isSubmittingTest
                      ? t("tasks.add.saving")
                      : t("tasks.add.save")}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}

      {/* Add Tax Modal */}
      {showAddTaxModal &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              zIndex: 999999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
            }}
            onClick={() => {
              setShowAddTaxModal(false);
              setTaxForm({
                name_ar: "",
                name_en: "",
                rate: "",
                is_active: true,
              });
            }}
          >
            <div
              style={{
                backgroundColor: "#1b3350",
                borderRadius: "12px",
                padding: "30px",
                width: "90%",
                maxWidth: "600px",
                maxHeight: "90vh",
                overflowY: "auto",
                border: "1px solid #333",
                direction: isRTL ? "rtl" : "ltr",
              }}
              onClick={e => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                  flexDirection: isRTL ? "row-reverse" : "row",
                }}
              >
                <h2
                  style={{
                    color: "white",
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: "bold",
                    textAlign: isRTL ? "right" : "left",
                  }}
                >
                  {t("quotations.add.modals.addTax.title", {
                    defaultValue: "إضافة ضريبة جديدة",
                  })}
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddTaxModal(false);
                    setTaxForm({
                      name_ar: "",
                      name_en: "",
                      rate: "",
                      is_active: true,
                    });
                  }}
                  style={{
                    backgroundColor: "transparent",
                    border: "none",
                    color: "white",
                    fontSize: "24px",
                    cursor: "pointer",
                    padding: 0,
                    width: "30px",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaTimes />
                </button>
              </div>
              <form onSubmit={handleAddTax}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                    gap: "20px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "bold",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addTax.nameAr", {
                        defaultValue: "الاسم بالعربية",
                      })}{" "}
                      <span style={{ color: "#dc3545" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name_ar"
                      value={taxForm.name_ar}
                      onChange={handleTaxFormChange}
                      onFocus={e => e.stopPropagation()}
                      onBlur={e => e.stopPropagation()}
                      required
                      style={{
                        width: "100%",
                        padding: "12px 15px",
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: "8px",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "bold",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addTax.nameEn", {
                        defaultValue: "الاسم بالإنجليزية",
                      })}{" "}
                      <span style={{ color: "#dc3545" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name_en"
                      value={taxForm.name_en}
                      onChange={handleTaxFormChange}
                      onFocus={e => e.stopPropagation()}
                      onBlur={e => e.stopPropagation()}
                      required
                      style={{
                        width: "100%",
                        padding: "12px 15px",
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: "8px",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "bold",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addTax.rate", {
                        defaultValue: "النسبة %",
                      })}{" "}
                      <span style={{ color: "#dc3545" }}>*</span>
                    </label>
                    <input
                      type="number"
                      name="rate"
                      value={taxForm.rate}
                      onChange={handleTaxFormChange}
                      onFocus={e => e.stopPropagation()}
                      onBlur={e => e.stopPropagation()}
                      required
                      style={{
                        width: "100%",
                        padding: "12px 15px",
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: "8px",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      marginTop: "30px",
                      flexDirection: isRTL ? "row-reverse" : "row",
                    }}
                  >
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={taxForm.is_active}
                      onChange={handleTaxFormChange}
                      style={{
                        width: "20px",
                        height: "20px",
                        cursor: "pointer",
                      }}
                    />
                    <label
                      style={{
                        color: "white",
                        fontSize: "14px",
                        cursor: "pointer",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addTax.isActive", {
                        defaultValue: "نشط",
                      })}
                    </label>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "15px",
                    marginTop: "30px",
                    flexDirection: isRTL ? "row-reverse" : "row",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddTaxModal(false);
                      setTaxForm({
                        name_ar: "",
                        name_en: "",
                        rate: "",
                        is_active: true,
                      });
                    }}
                    style={{
                      backgroundColor: "#6c757d",
                      color: "white",
                      border: "none",
                      padding: "12px 25px",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      cursor: "pointer",
                    }}
                  >
                    {t("quotations.add.modals.addTax.cancel", {
                      defaultValue: "إلغاء",
                    })}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingTax}
                    style={{
                      backgroundColor: isSubmittingTax ? "#666" : "#AC2000",
                      color: "white",
                      border: "none",
                      padding: "12px 25px",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      cursor: isSubmittingTax ? "not-allowed" : "pointer",
                      opacity: isSubmittingTax ? 0.7 : 1,
                    }}
                  >
                    {isSubmittingTax
                      ? t("quotations.add.modals.addTax.saving", {
                          defaultValue: "جاري الحفظ...",
                        })
                      : t("quotations.add.modals.addTax.save", {
                          defaultValue: "حفظ",
                        })}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}

      {/* Add Cost Center Modal */}
      {showAddCostCenterModal &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              zIndex: 999999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
            }}
            onClick={() => {
              setShowAddCostCenterModal(false);
              setCostCenterForm({
                name_ar: "",
                name_en: "",
                code: "",
              });
            }}
          >
            <div
              style={{
                backgroundColor: "#1b3350",
                borderRadius: "12px",
                padding: "30px",
                width: "90%",
                maxWidth: "600px",
                maxHeight: "90vh",
                overflowY: "auto",
                border: "1px solid #333",
                direction: isRTL ? "rtl" : "ltr",
              }}
              onClick={e => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                  flexDirection: isRTL ? "row-reverse" : "row",
                }}
              >
                <h2
                  style={{
                    color: "white",
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: "bold",
                    textAlign: isRTL ? "right" : "left",
                  }}
                >
                  {t("quotations.add.modals.addCostCenter.title", {
                    defaultValue: "إضافة مركز تكلفة جديد",
                  })}
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddCostCenterModal(false);
                    setCostCenterForm({
                      name_ar: "",
                      name_en: "",
                      code: "",
                    });
                  }}
                  style={{
                    backgroundColor: "transparent",
                    border: "none",
                    color: "white",
                    fontSize: "24px",
                    cursor: "pointer",
                    padding: 0,
                    width: "30px",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaTimes />
                </button>
              </div>
              <form onSubmit={handleAddCostCenter}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                    gap: "20px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "bold",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addCostCenter.nameAr", {
                        defaultValue: "الاسم بالعربية",
                      })}{" "}
                      <span style={{ color: "#dc3545" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name_ar"
                      value={costCenterForm.name_ar}
                      onChange={handleCostCenterFormChange}
                      onFocus={e => e.stopPropagation()}
                      onBlur={e => e.stopPropagation()}
                      required
                      style={{
                        width: "100%",
                        padding: "12px 15px",
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: "8px",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "bold",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addCostCenter.nameEn", {
                        defaultValue: "الاسم بالإنجليزية",
                      })}{" "}
                      <span style={{ color: "#dc3545" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name_en"
                      value={costCenterForm.name_en}
                      onChange={handleCostCenterFormChange}
                      onFocus={e => e.stopPropagation()}
                      onBlur={e => e.stopPropagation()}
                      required
                      style={{
                        width: "100%",
                        padding: "12px 15px",
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: "8px",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "bold",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addCostCenter.code", {
                        defaultValue: "الكود",
                      })}
                    </label>
                    <input
                      type="text"
                      name="code"
                      value={costCenterForm.code}
                      onChange={handleCostCenterFormChange}
                      onFocus={e => e.stopPropagation()}
                      onBlur={e => e.stopPropagation()}
                      style={{
                        width: "100%",
                        padding: "12px 15px",
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: "8px",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "15px",
                    marginTop: "30px",
                    flexDirection: isRTL ? "row-reverse" : "row",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddCostCenterModal(false);
                      setCostCenterForm({
                        name_ar: "",
                        name_en: "",
                        code: "",
                      });
                    }}
                    style={{
                      backgroundColor: "#6c757d",
                      color: "white",
                      border: "none",
                      padding: "12px 25px",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      cursor: "pointer",
                    }}
                  >
                    {t("quotations.add.modals.addCostCenter.cancel", {
                      defaultValue: "إلغاء",
                    })}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingCostCenter}
                    style={{
                      backgroundColor: isSubmittingCostCenter
                        ? "#666"
                        : "#AC2000",
                      color: "white",
                      border: "none",
                      padding: "12px 25px",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      cursor: isSubmittingCostCenter
                        ? "not-allowed"
                        : "pointer",
                      opacity: isSubmittingCostCenter ? 0.7 : 1,
                    }}
                  >
                    {isSubmittingCostCenter
                      ? t("quotations.add.modals.addCostCenter.saving", {
                          defaultValue: "جاري الحفظ...",
                        })
                      : t("quotations.add.modals.addCostCenter.save", {
                          defaultValue: "حفظ",
                        })}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}

      {/* Add Customer Modal */}
      {showAddCustomerModal &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              zIndex: 999999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
            }}
            onClick={() => setShowAddCustomerModal(false)}
          >
            <div
              style={{
                backgroundColor: "#1b3350",
                borderRadius: "12px",
                padding: "30px",
                maxWidth: "900px",
                width: "100%",
                maxHeight: "90vh",
                overflowY: "auto",
                border: "1px solid #333",
                direction: isRTL ? "rtl" : "ltr",
              }}
              onClick={e => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                  flexDirection: isRTL ? "row-reverse" : "row",
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontSize: "20px",
                    color: "white",
                    textAlign: isRTL ? "right" : "left",
                  }}
                >
                  {t("quotations.add.modals.addCustomer.title", {
                    defaultValue: "إضافة عميل جديد",
                  })}
                </h2>
                <button
                  onClick={() => {
                    setShowAddCustomerModal(false);
                    setCustomerForm({
                      name_en: "",
                      name_ar: "",
                      code: "",
                      contact_person: "",
                      email: "",
                      phone: "",
                      address: "",
                      tax_number: "",
                      is_active: true,
                      notes: "",
                    });
                  }}
                  style={{
                    backgroundColor: "transparent",
                    border: "none",
                    color: "white",
                    fontSize: "24px",
                    cursor: "pointer",
                    padding: "0",
                    width: "30px",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaTimes />
                </button>
              </div>
              <form onSubmit={handleAddCustomer}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                    gap: "20px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "bold",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addCustomer.nameEn", {
                        defaultValue: "الاسم بالإنجليزية",
                      })}{" "}
                      <span style={{ color: "#dc3545" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name_en"
                      value={customerForm.name_en}
                      onChange={handleCustomerFormChange}
                      onFocus={e => e.stopPropagation()}
                      onBlur={e => e.stopPropagation()}
                      required
                      style={{
                        width: "100%",
                        padding: "12px 15px",
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: "8px",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "bold",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addCustomer.nameAr", {
                        defaultValue: "الاسم بالعربية",
                      })}{" "}
                      <span style={{ color: "#dc3545" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name_ar"
                      value={customerForm.name_ar}
                      onChange={handleCustomerFormChange}
                      onFocus={e => e.stopPropagation()}
                      onBlur={e => e.stopPropagation()}
                      required
                      style={{
                        width: "100%",
                        padding: "12px 15px",
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: "8px",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "bold",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addCustomer.code", {
                        defaultValue: "الكود",
                      })}
                    </label>
                    <input
                      type="text"
                      name="code"
                      value={customerForm.code}
                      onChange={handleCustomerFormChange}
                      onFocus={e => e.stopPropagation()}
                      onBlur={e => e.stopPropagation()}
                      style={{
                        width: "100%",
                        padding: "12px 15px",
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: "8px",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "bold",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addCustomer.contactPerson", {
                        defaultValue: "الشخص المسؤول",
                      })}
                    </label>
                    <input
                      type="text"
                      name="contact_person"
                      value={customerForm.contact_person}
                      onChange={handleCustomerFormChange}
                      onFocus={e => e.stopPropagation()}
                      onBlur={e => e.stopPropagation()}
                      style={{
                        width: "100%",
                        padding: "12px 15px",
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: "8px",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "bold",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addCustomer.email", {
                        defaultValue: "البريد الإلكتروني",
                      })}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={customerForm.email}
                      onChange={handleCustomerFormChange}
                      onFocus={e => e.stopPropagation()}
                      onBlur={e => e.stopPropagation()}
                      style={{
                        width: "100%",
                        padding: "12px 15px",
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: "8px",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "bold",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addCustomer.phone", {
                        defaultValue: "الهاتف",
                      })}
                    </label>
                    <input
                      type="text"
                      name="phone"
                      value={customerForm.phone}
                      onChange={handleCustomerFormChange}
                      onFocus={e => e.stopPropagation()}
                      onBlur={e => e.stopPropagation()}
                      style={{
                        width: "100%",
                        padding: "12px 15px",
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: "8px",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "bold",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addCustomer.address", {
                        defaultValue: "العنوان",
                      })}
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={customerForm.address}
                      onChange={handleCustomerFormChange}
                      onFocus={e => e.stopPropagation()}
                      onBlur={e => e.stopPropagation()}
                      style={{
                        width: "100%",
                        padding: "12px 15px",
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: "8px",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "bold",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addCustomer.taxNumber", {
                        defaultValue: "الرقم الضريبي",
                      })}
                    </label>
                    <input
                      type="text"
                      name="tax_number"
                      value={customerForm.tax_number}
                      onChange={handleCustomerFormChange}
                      onFocus={e => e.stopPropagation()}
                      onBlur={e => e.stopPropagation()}
                      style={{
                        width: "100%",
                        padding: "12px 15px",
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: "8px",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      flexDirection: isRTL ? "row-reverse" : "row",
                    }}
                  >
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={customerForm.is_active}
                      onChange={handleCustomerFormChange}
                      style={{
                        width: "20px",
                        height: "20px",
                        cursor: "pointer",
                      }}
                    />
                    <label
                      style={{
                        color: "white",
                        fontSize: "14px",
                        cursor: "pointer",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addCustomer.isActive", {
                        defaultValue: "نشط",
                      })}
                    </label>
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "bold",
                        color: "white",
                        fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("quotations.add.modals.addCustomer.notes", {
                        defaultValue: "ملاحظات",
                      })}
                    </label>
                    <textarea
                      name="notes"
                      value={customerForm.notes}
                      onChange={handleCustomerFormChange}
                      onFocus={e => e.stopPropagation()}
                      onBlur={e => e.stopPropagation()}
                      rows={4}
                      style={{
                        width: "100%",
                        padding: "12px 15px",
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderRadius: "8px",
                        color: "white",
                        fontSize: "14px",
                        resize: "vertical",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "15px",
                    marginTop: "30px",
                    flexDirection: isRTL ? "row-reverse" : "row",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddCustomerModal(false);
                      setCustomerForm({
                        name_en: "",
                        name_ar: "",
                        code: "",
                        contact_person: "",
                        email: "",
                        phone: "",
                        address: "",
                        tax_number: "",
                        is_active: true,
                        notes: "",
                      });
                    }}
                    style={{
                      backgroundColor: "#6c757d",
                      color: "white",
                      border: "none",
                      padding: "12px 25px",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      cursor: "pointer",
                    }}
                  >
                    {t("quotations.add.modals.addCustomer.cancel", {
                      defaultValue: "إلغاء",
                    })}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingCustomer}
                    style={{
                      backgroundColor: isSubmittingCustomer
                        ? "#666"
                        : "#AC2000",
                      color: "white",
                      border: "none",
                      padding: "12px 25px",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      cursor: isSubmittingCustomer ? "not-allowed" : "pointer",
                      opacity: isSubmittingCustomer ? 0.7 : 1,
                    }}
                  >
                    {isSubmittingCustomer
                      ? t("quotations.add.modals.addCustomer.saving", {
                          defaultValue: "جاري الحفظ...",
                        })
                      : t("quotations.add.modals.addCustomer.save", {
                          defaultValue: "حفظ",
                        })}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ProjectTests;
